import React, { useEffect, useState, useCallback, useMemo, useRef, useTransition } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../utils/api';
import {
  BarChart,
  Bar as ReBar,
  XAxis,
  YAxis,
  Tooltip as ReTooltip,
  CartesianGrid,
  ResponsiveContainer,
  ComposedChart,
  LabelList,
} from 'recharts';
import { useResizableColumns, ResizeHandle, useSortableData, SortableHeader } from '../components/shared/tableUtils';
import Pagination from '../components/shared/Pagination';
import FilterBar, { dateRangeForFilter, DEFAULT_DATE_PRESETS } from '../components/shared/FilterBar';
import { useAuth } from '../context/AuthContext';
import { PERSONAL_EMAIL_DOMAINS, PERSONAL_EMAIL_BUCKET_LABEL } from '../utils/personalEmailDomains';
import { InquiryConversionChart } from '../components/dashboard/DashboardCharts';
import BookingLinkTab from '../components/mediator/BookingLinkTab';
import DocumentsTab from '../components/mediator/DocumentsTab';
import ProfileTab from '../components/mediator/ProfileTab';

const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/* ── Minimal toast ─────────────────────────────────────────────────── */
function Toast({ message, type, onDone }) {
  useEffect(() => {
    const t = setTimeout(onDone, 3000);
    return () => clearTimeout(t);
  }, [onDone]);
  const bg = type === 'error' ? '#fde8e9' : '#e8fdf5';
  const color = type === 'error' ? '#dc2626' : '#115e59';
  return (
    <div
      style={{
        position: 'fixed', bottom: 24, right: 24, zIndex: 9999,
        background: bg, color, borderRadius: 8, padding: '10px 16px',
        fontFamily: 'Inter, sans-serif', fontSize: 13, fontWeight: 500,
        boxShadow: '0 4px 12px rgba(0,0,0,0.12)', pointerEvents: 'none',
      }}
    >
      {message}
    </div>
  );
}

// Round a change-pill percentage; pass null/undefined (the "+N new" /
// isNew case) through untouched. NOT bounded — shows the real value.
const roundPct = (p) => (p == null ? p : Math.round(p));

/* ── KPI MoM badge — copied 1:1 from Dashboard's ChangeBadge ─────── */
function ChangeBadge({ change, invertColors, compareLabel }) {
  const dir = change?.direction || 'flat';
  // Display the real percent (rounded), uncapped — may exceed ±100.
  const pct = roundPct(change?.percent);
  const isNew = change?.isNew;
  const isDown = dir === 'down';
  const isFlat = dir === 'flat';
  const isGood = invertColors ? isDown : !isDown;
  const bg = isFlat ? '#f3f4f6' : (isGood ? '#e8fdf5' : '#fde8e9');
  const color = isFlat ? '#6b7280' : (isGood ? '#00a666' : '#dc2626');
  let label;
  if (isNew) {
    label = `+${change.value} new`;
  } else if (typeof pct === 'number') {
    const sign = pct > 0 ? '+' : '';
    label = `${sign}${pct}%`;
  } else {
    label = '—';
  }
  return (
    <span className="inline-flex items-center gap-[5px] rounded-full" style={{ backgroundColor: bg, padding: '4px 8px 5px 8px' }}>
      <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
        {isDown ? (
          <>
            <path d="M1.5 3.5L4.5 6.5L6.5 4.5L10.5 8.5" stroke={color} strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M7.5 8.5H10.5V5.5" stroke={color} strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
          </>
        ) : (
          <>
            <path d="M1.5 8.5L4.5 5.5L6.5 7.5L10.5 3.5" stroke={color} strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M7.5 3.5H10.5V6.5" stroke={color} strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
          </>
        )}
      </svg>
      <span className="flex items-center gap-[3px]">
        <span className="text-[10px] font-medium" style={{ fontFamily: 'Inter Tight', color }}>{label}</span>
        <span className="text-[10px] font-medium text-[#0e1e20]" style={{ fontFamily: 'Inter Tight' }}>vs</span>
        <span className="text-[10px] font-medium text-[#0e1e20]" style={{ fontFamily: 'Inter Tight' }}>{compareLabel || change?.compareLabel || 'last month'}</span>
      </span>
    </span>
  );
}

/* ── Case Manager Fee Editor (compact, top-right) ──────────────────── */
function FeeEditor({ mediatorId, initialFeeType, initialFeeAmount, onSaved }) {
  const [open, setOpen] = useState(false);
  const [feeType, setFeeType] = useState(initialFeeType || 'fixed');
  const [feeAmount, setFeeAmount] = useState(
    initialFeeAmount != null ? String(initialFeeAmount) : ''
  );
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const wrapRef = useRef(null);

  useEffect(() => {
    setFeeType(initialFeeType || 'fixed');
    setFeeAmount(initialFeeAmount != null ? String(initialFeeAmount) : '');
  }, [initialFeeType, initialFeeAmount]);

  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const isDirty =
    feeType !== (initialFeeType || 'fixed') ||
    feeAmount !== (initialFeeAmount != null ? String(initialFeeAmount) : '');

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.patch(`/mediators/${mediatorId}/case-manager-fee`, {
        case_manager_fee_type: feeType,
        case_manager_fee_amount: feeAmount !== '' ? Number(feeAmount) : null,
      });
      setToast({ message: 'Fee saved', type: 'success' });
      setOpen(false);
      if (onSaved) onSaved();
    } catch (err) {
      const message = err?.response?.data?.message || 'Failed to save fee';
      setToast({ message, type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setFeeType(initialFeeType || 'fixed');
    setFeeAmount(initialFeeAmount != null ? String(initialFeeAmount) : '');
    setOpen(false);
  };

  const hasFee = initialFeeType && initialFeeAmount != null;
  const valueLabel = hasFee
    ? (initialFeeType === 'fixed'
        ? `$${Number(initialFeeAmount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
        : `${Number(initialFeeAmount)}%`)
    : 'Not set';

  return (
    <>
      {toast && <Toast message={toast.message} type={toast.type} onDone={() => setToast(null)} />}
      {/* Card layout matches the design: bordered pill with label on top,
          value + "/case" beneath, separated mint pencil square on the
          right. Whole card opens the popover. */}
      <div className="relative" ref={wrapRef}>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="flex items-center gap-2 rounded-[10px] border border-[#e5e5e5] bg-white hover:bg-[#f9fafb] transition-colors"
          style={{ padding: '10px 12px', minWidth: 200 }}
        >
          <div className="flex flex-col items-start gap-0.5 flex-1">
            <span className="text-[11px] font-medium" style={{ fontFamily: 'Inter, sans-serif', color: '#9ca3af' }}>
              Case Manager Fee
            </span>
            <div className="flex items-baseline gap-1">
              <span className="text-[#0e1e20] text-[18px] font-semibold leading-none" style={{ fontFamily: "'Inter Tight', sans-serif" }}>
                {valueLabel}
              </span>
              {hasFee && (
                <span className="text-[#9ca3af] text-[12px]" style={{ fontFamily: 'Inter, sans-serif' }}>
                  /{initialFeeType === 'fixed' ? 'case' : 'rev'}
                </span>
              )}
            </div>
          </div>
          <span
            aria-label="Edit fee"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 30,
              height: 30,
              borderRadius: 7,
              background: '#f0fdfa',
              border: '1px solid #b7ebdf',
              color: '#115e59',
              flexShrink: 0,
            }}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
              <path
                d="M16.862 4.487l1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L6.832 19.82a4.5 4.5 0 0 1-1.897 1.13l-2.685.8.8-2.685a4.5 4.5 0 0 1 1.13-1.897L16.863 4.487zm0 0L19.5 7.125"
                stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"
              />
            </svg>
          </span>
        </button>

        {open && (
          <div
            className="absolute"
            style={{
              // Above both drawer overlay (z-40) and drawer body (z-50)
              // so a popped-open fee editor can't get visually trapped
              // behind a freshly opened firm drawer.
              zIndex: 60,
              top: 'calc(100% + 8px)',
              right: 0,
              width: 280,
              background: '#fff',
              border: '1px solid #e5e5e5',
              borderRadius: 10,
              boxShadow: '0 8px 24px rgba(16,24,40,0.12), 0 2px 6px rgba(16,24,40,0.06)',
              padding: 14,
            }}
          >
            <div className="flex items-center justify-between mb-2.5">
              <span className="text-[#0e1e20] text-[13px] font-semibold" style={{ fontFamily: "'Inter Tight', sans-serif" }}>
                Set Fee
              </span>
              <button
                type="button"
                onClick={handleCancel}
                className="text-[#9ca3af] hover:text-[#0e1e20]"
                style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 2, lineHeight: 0 }}
              >
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <path d="M3.5 3.5l7 7M10.5 3.5l-7 7" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
                </svg>
              </button>
            </div>
            <div className="grid grid-cols-2 gap-1 p-1 rounded-lg mb-3" style={{ background: '#f3f4f6' }}>
              {['fixed', 'percentage'].map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setFeeType(t)}
                  style={{
                    fontFamily: 'Inter, sans-serif', fontSize: 12, fontWeight: 600,
                    padding: '6px 10px', borderRadius: 6, border: 'none', cursor: 'pointer',
                    background: feeType === t ? '#fff' : 'transparent',
                    color: feeType === t ? '#115e59' : '#5b5a64',
                    boxShadow: feeType === t ? '0 1px 2px rgba(16,24,40,0.08)' : 'none',
                  }}
                >
                  {t === 'fixed' ? 'Fixed' : 'Percentage'}
                </button>
              ))}
            </div>
            <label className="block text-[11px] font-medium mb-1" style={{ fontFamily: 'Inter, sans-serif', color: '#5b5a64' }}>
              {feeType === 'fixed' ? 'Amount per case' : 'Percentage per case'}
            </label>
            <div
              className="flex items-center mb-3"
              style={{
                border: '1px solid #e5e5e5', borderRadius: 8, background: '#fff',
                paddingLeft: feeType === 'fixed' ? 10 : 12,
                paddingRight: feeType === 'percentage' ? 10 : 12,
                height: 36,
              }}
            >
              {feeType === 'fixed' && (
                <span className="text-[#5b5a64] text-sm mr-1" style={{ fontFamily: 'Inter, sans-serif' }}>$</span>
              )}
              <input
                type="number" min="0" value={feeAmount}
                onChange={(e) => setFeeAmount(e.target.value)}
                placeholder="0" autoFocus
                style={{ flex: 1, fontFamily: 'Inter, sans-serif', fontSize: 14, color: '#0e1e20', border: 'none', outline: 'none', background: 'transparent', width: '100%' }}
              />
              {feeType === 'percentage' && (
                <span className="text-[#5b5a64] text-sm ml-1" style={{ fontFamily: 'Inter, sans-serif' }}>%</span>
              )}
            </div>
            <div className="flex items-center justify-end gap-2">
              <button
                type="button" onClick={handleCancel} disabled={saving}
                style={{ fontFamily: 'Inter, sans-serif', fontSize: 12, fontWeight: 600, padding: '6px 12px', borderRadius: 6, border: '1px solid #e5e5e5', background: '#fff', color: '#5b5a64', cursor: saving ? 'not-allowed' : 'pointer' }}
              >
                Cancel
              </button>
              <button
                type="button" onClick={handleSave} disabled={!isDirty || saving}
                style={{ fontFamily: 'Inter, sans-serif', fontSize: 12, fontWeight: 600, padding: '6px 14px', borderRadius: 6, border: 'none', cursor: isDirty && !saving ? 'pointer' : 'not-allowed', background: isDirty && !saving ? '#115e59' : '#e5e5e5', color: isDirty && !saving ? '#fff' : '#9ca3af' }}
              >
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

// Filter state and pills are now provided by the shared FilterBar
// component (used by Dashboard / Mediators / Cases / Inquiries) — see
// the call-site at the bottom of MediatorDetail for wiring.

/* ── Chart helpers ─────────────────────────────────────────────────── */
function parseDateLoose(s) {
  if (!s) return null;
  const d = s.length === 10 ? new Date(`${s}T00:00:00Z`) : new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
}

function pad2(n) { return n < 10 ? `0${n}` : `${n}`; }

// Generate monthly buckets spanning an active date range, or fall back
// to "last N months from today" when no range is set. Buckets are
// inclusive on both ends and capped at MAX so very wide ranges still
// render readably.
function buildMonthBuckets(dateRange, fallbackCount = 6, maxBuckets = 24) {
  const { startDate, endDate } = dateRange || {};
  let startY, startM, endY, endM;
  if (startDate || endDate) {
    // Parse range bounds as UTC midnight to stay consistent with the
    // case-aggregation loops (which key off getUTCFullYear/getUTCMonth).
    // Mixing local and UTC parsing causes off-by-one-month bucketing for
    // users east of UTC.
    const now = new Date();
    const e = endDate
      ? new Date(`${endDate}T00:00:00Z`)
      : now;
    const s = startDate
      ? new Date(`${startDate}T00:00:00Z`)
      : new Date(Date.UTC(e.getUTCFullYear(), e.getUTCMonth() - (fallbackCount - 1), 1));
    startY = s.getUTCFullYear(); startM = s.getUTCMonth();
    endY = e.getUTCFullYear(); endM = e.getUTCMonth();
  } else {
    const now = new Date();
    endY = now.getUTCFullYear(); endM = now.getUTCMonth();
    const startDt = new Date(Date.UTC(endY, endM - (fallbackCount - 1), 1));
    startY = startDt.getUTCFullYear(); startM = startDt.getUTCMonth();
  }
  // Defensive: an inverted range (end < start) would otherwise loop until
  // maxBuckets and emit empty placeholders. Return nothing instead.
  if (endY < startY || (endY === startY && endM < startM)) return [];
  const buckets = [];
  let y = startY;
  let m = startM;
  for (let i = 0; i < maxBuckets; i++) {
    const includeYear = startY !== endY;
    buckets.push({
      year: y,
      month: m,
      key: `${y}-${pad2(m + 1)}`,
      label: includeYear ? `${MONTH_SHORT[m]} ${String(y).slice(2)}` : MONTH_SHORT[m],
    });
    if (y === endY && m === endM) break;
    m += 1;
    if (m > 11) { m = 0; y += 1; }
  }
  return buckets;
}

// Inclusive day span of a {startDate, endDate} range, or null if unbounded/invalid.
function rangeDaySpan(dateRange) {
  const { startDate, endDate } = dateRange || {};
  if (!startDate || !endDate) return null;
  const s = new Date(`${startDate}T00:00:00Z`);
  const e = new Date(`${endDate}T00:00:00Z`);
  if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime()) || e < s) return null;
  return Math.floor((e.getTime() - s.getTime()) / 86400000) + 1;
}

// True when the range should render as DAILY bars — matches the dashboard's
// bucketsForRange rule (≤ 31 days → one bar per day instead of one monthly bar).
function isDailyRange(dateRange) {
  const span = rangeDaySpan(dateRange);
  return span != null && span >= 1 && span <= 31;
}

// One bucket per calendar day in the range. key 'YYYY-MM-DD', label 'Jun 1'.
function buildDayBuckets(dateRange, maxDays = 62) {
  const { startDate, endDate } = dateRange || {};
  if (!startDate || !endDate) return [];
  const cur = new Date(`${startDate}T00:00:00Z`);
  const end = new Date(`${endDate}T00:00:00Z`);
  const out = [];
  while (cur.getTime() <= end.getTime() && out.length < maxDays) {
    out.push({
      key: `${cur.getUTCFullYear()}-${pad2(cur.getUTCMonth() + 1)}-${pad2(cur.getUTCDate())}`,
      label: `${MONTH_SHORT[cur.getUTCMonth()]} ${cur.getUTCDate()}`,
      bucketType: 'day',
    });
    cur.setUTCDate(cur.getUTCDate() + 1);
  }
  return out;
}

// Aggregation key for a Date at the chart's current granularity.
const dayKey = (d) => `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())}`;
const monthKey = (d) => `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}`;

// Generate ISO-week buckets spanning an active date range (Mon-start)
// or fall back to "last N weeks ending this week". Caps at maxBuckets
// (chart compression past ~16 weekly bars hurts readability).
function buildWeekBuckets(dateRange, fallbackCount = 8, maxBuckets = 16) {
  const startOfWeek = (d) => {
    const x = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
    const dow = (x.getUTCDay() + 6) % 7;
    x.setUTCDate(x.getUTCDate() - dow);
    return x;
  };
  const { startDate, endDate } = dateRange || {};
  let firstStart;
  let lastStart;
  if (startDate || endDate) {
    const now = new Date();
    const e = endDate ? new Date(`${endDate}T00:00:00Z`) : now;
    const s = startDate
      ? new Date(`${startDate}T00:00:00Z`)
      : (() => {
          const tmp = new Date(e);
          tmp.setUTCDate(tmp.getUTCDate() - (fallbackCount - 1) * 7);
          return tmp;
        })();
    firstStart = startOfWeek(s);
    lastStart = startOfWeek(e);
  } else {
    const now = new Date();
    lastStart = startOfWeek(now);
    firstStart = new Date(lastStart);
    firstStart.setUTCDate(firstStart.getUTCDate() - (fallbackCount - 1) * 7);
  }
  // Defensive: inverted range — return nothing instead of silently
  // rendering an empty chart with no explanation.
  if (lastStart < firstStart) return [];
  const totalWeeks = Math.floor((lastStart - firstStart) / (7 * 86400000)) + 1;
  // If the range is wider than maxBuckets, only render the last
  // maxBuckets weeks (anchored at lastStart) so bars stay legible.
  // Stamp `_truncated` + `_totalWeeks` on the array so the chart can
  // surface a hint to the user instead of silently dropping data.
  const startIdx = Math.max(0, totalWeeks - maxBuckets);
  const buckets = [];
  for (let i = startIdx; i < totalWeeks; i++) {
    const start = new Date(firstStart);
    start.setUTCDate(start.getUTCDate() + i * 7);
    const end = new Date(start);
    end.setUTCDate(end.getUTCDate() + 6);
    const key = `${start.getUTCFullYear()}-${pad2(start.getUTCMonth() + 1)}-${pad2(start.getUTCDate())}`;
    buckets.push({
      key,
      label: `${MONTH_SHORT[start.getUTCMonth()]} ${start.getUTCDate()}`,
      startISO: key,
      endISO: `${end.getUTCFullYear()}-${pad2(end.getUTCMonth() + 1)}-${pad2(end.getUTCDate())}`,
    });
  }
  buckets._truncated = startIdx > 0;
  buckets._totalCount = totalWeeks;
  buckets._shownCount = buckets.length;
  return buckets;
}

// Fit a weekly window to the data (matches Scale): span earliest→latest case
// week, clamped to [minWeeks, maxWeeks] and anchored at the latest (or current)
// week, so the weekly x-axis stays readable instead of cramming ~26 weeks.
function fitWeekWindow(dates, { minWeeks = 8, maxWeeks = 16, now = new Date() } = {}) {
  const WEEK = 7 * 86400000;
  const startOfWeek = (d) => {
    const x = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
    x.setUTCDate(x.getUTCDate() - ((x.getUTCDay() + 6) % 7));
    return x;
  };
  const fmt = (d) => `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())}`;
  const cur = startOfWeek(now);
  const valid = (dates || []).filter(Boolean);
  let earliest = cur;
  let latest = cur;
  if (valid.length) {
    earliest = startOfWeek(valid.reduce((a, d) => (d < a ? d : a), valid[0]));
    latest = startOfWeek(valid.reduce((a, d) => (d > a ? d : a), valid[0]));
  }
  const end = latest > cur ? latest : cur;
  let start = earliest < end ? earliest : end;
  const weeksInclusive = (a, b) => Math.floor((b - a) / WEEK) + 1;
  if (weeksInclusive(start, end) < minWeeks) start = new Date(end.getTime() - (minWeeks - 1) * WEEK);
  if (weeksInclusive(start, end) > maxWeeks) start = new Date(end.getTime() - (maxWeeks - 1) * WEEK);
  return { startDate: fmt(start), endDate: fmt(end) };
}

function fmtMoneyShort(n) {
  if (!n && n !== 0) return '$0';
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(n % 1_000_000 === 0 ? 0 : 1)}M`;
  if (n >= 1_000) {
    const k = n / 1_000;
    return `$${k >= 100 ? Math.round(k) : k.toFixed(k >= 10 ? 0 : 1)}k`;
  }
  return `$${Math.round(n)}`;
}

function fmtMoneyFull(n) { return `$${Math.round(n || 0).toLocaleString('en-US')}`; }

/* ── Per-chart time-range dropdown ──────────────────────────────────────
 *
 * Renders a small button in the top-right of a chart header that opens a
 * popover with 1M / 3M / 6M / YTD. The selected key is the chart's own
 * state — it OVERRIDES the page-level Date pill so each chart can be
 * focused independently of the others. Returned date range uses the same
 * { startDate: 'YYYY-MM-DD', endDate: 'YYYY-MM-DD' } shape that
 * buildMonthBuckets / buildWeekBuckets already consume.
 */
const CHART_RANGE_OPTIONS = [
  { key: 'all', label: 'All', days: 365 },
  { key: '1m',  label: '1M',  days: 30  },
  { key: '3m',  label: '3M',  days: 90  },
  { key: '6m',  label: '6M',  days: 180 },
  { key: 'ytd', label: 'YTD', days: null },
];

function chartRangeToDateRange(rangeKey, now = new Date(), { centered = false } = {}) {
  // Calendar-month aligned. When `centered` is true and rangeKey is 'all',
  // the 12-bucket window puts the current month at position 8 — 7 past +
  // current + 4 future — so upcoming scheduled mediations are visible on
  // the right side of the chart (MoM Trend). Other charts (Booking Trend)
  // omit `centered` so the window stops at the current month with no
  // empty future buckets.
  const today = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
  let start;
  let endOfMonth;
  if (rangeKey === 'all' && centered) {
    start = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth() - 7, 1));
    endOfMonth = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth() + 5, 0));
  } else {
    endOfMonth = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth() + 1, 0));
    if (rangeKey === 'ytd') {
      start = new Date(Date.UTC(today.getUTCFullYear(), 0, 1));
    } else if (rangeKey === '1m') {
      start = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), 1));
    } else if (rangeKey === '3m') {
      start = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth() - 2, 1));
    } else if (rangeKey === '6m') {
      start = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth() - 5, 1));
    } else {
      // 'all' (non-centered) — last 12 calendar months ending at current month.
      start = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth() - 11, 1));
    }
  }
  const endDate = `${endOfMonth.getUTCFullYear()}-${pad2(endOfMonth.getUTCMonth() + 1)}-${pad2(endOfMonth.getUTCDate())}`;
  const startDate = `${start.getUTCFullYear()}-${pad2(start.getUTCMonth() + 1)}-${pad2(start.getUTCDate())}`;
  return { startDate, endDate };
}

function ChartRangeDropdown({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);
  const current = CHART_RANGE_OPTIONS.find((o) => o.key === value) || CHART_RANGE_OPTIONS[0];
  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          fontFamily: 'Inter, sans-serif', fontSize: 11, fontWeight: 600,
          padding: '5px 10px', borderRadius: 6,
          border: '1px solid #e5e5e5', background: '#fff', color: '#0e1e20',
          cursor: 'pointer', lineHeight: 1,
        }}
      >
        {current.label}
        <svg width="9" height="6" viewBox="0 0 10 6" fill="none" aria-hidden="true">
          <path d="M1 1l4 4 4-4" stroke="#5b5a64" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {open && (
        <div
          role="listbox"
          style={{
            position: 'absolute', top: 'calc(100% + 4px)', right: 0,
            background: '#fff', border: '1px solid #e5e5e5', borderRadius: 8,
            boxShadow: '0 4px 12px rgba(16,24,40,0.08)', zIndex: 20,
            minWidth: 84, padding: 4,
          }}
        >
          {CHART_RANGE_OPTIONS.map((opt) => (
            <button
              key={opt.key}
              role="option"
              aria-selected={opt.key === value}
              type="button"
              onClick={() => { onChange(opt.key); setOpen(false); }}
              style={{
                display: 'block', width: '100%', textAlign: 'left',
                fontFamily: 'Inter, sans-serif', fontSize: 12,
                padding: '6px 10px', borderRadius: 4, border: 'none',
                background: opt.key === value ? '#f3f4f6' : 'transparent',
                color: '#0e1e20', cursor: 'pointer', lineHeight: 1.4,
              }}
              onMouseEnter={(e) => { if (opt.key !== value) e.currentTarget.style.background = '#fafafa'; }}
              onMouseLeave={(e) => { if (opt.key !== value) e.currentTarget.style.background = 'transparent'; }}
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ── Month-over-Month Trend (Cases + Income, last 6 months) ────────── */
function MoMTrendChart({ cases, dateRange, onDrillDown, subtitle, granularity: granularityProp, onGranularityChange }) {
  // Weekly/monthly toggle — lifted to the parent (like BookingTrendChart) so the
  // choice survives sibling remounts; falls back to local state.
  const [localGranularity, setLocalGranularity] = useState('monthly');
  const granularity = granularityProp ?? localGranularity;
  const setGranularity = onGranularityChange ?? setLocalGranularity;
  // Per-chart range — used only when the page-level top FilterBar is on "All
  // time". When the top filter is active it drives the x-axis instead (below).
  const [chartRange, setChartRange] = useState('all');
  // `centered: true` puts the current month at position 8 in the 'all'
  // 12-bucket layout — 7 past + current + 4 future.
  const chartDateRange = useMemo(() => chartRangeToDateRange(chartRange, new Date(), { centered: true }), [chartRange]);
  const bucketCount = chartRange === '1m' ? 1 : chartRange === '3m' ? 3 : chartRange === '6m' ? 6 : 12;

  const data = useMemo(() => {
    // The page-level top filter (dateRange) takes precedence: when active the
    // chart x-axis spans EXACTLY the selected period, like the dashboard
    // (≤31-day window → one bar per DAY, else monthly). The weekly/monthly
    // toggle only applies when the top filter is "All time".
    const pageRange = dateRange && (dateRange.startDate || dateRange.endDate) ? dateRange : null;
    const accumulate = (buckets, keyFor) => {
      const byKey = new Map(buckets.map((b) => [b.key, b]));
      for (const c of cases || []) {
        const m = parseDateLoose(c.mediation_date);
        if (!m) continue;
        const b = byKey.get(keyFor(m));
        if (b) {
          b.cases += 1;
          const rev = Number(c.revenueInvoiced) || 0;
          if (rev > 0) b.income += rev;
        }
      }
      return buckets;
    };
    if (pageRange || isDailyRange(pageRange || chartDateRange)) {
      const range = pageRange || chartDateRange;
      const daily = isDailyRange(range);
      const buckets = (daily ? buildDayBuckets(range) : buildMonthBuckets(range, bucketCount, 24))
        .map((b) => ({ ...b, cases: 0, income: 0 }));
      return accumulate(buckets, (m) => (daily ? dayKey(m) : monthKey(m)));
    }
    if (granularity === 'weekly') {
      // Fit the weekly window to the data (≤16 weeks) so the x-axis stays
      // readable, matching the Scale portal.
      const medDates = (cases || []).map((c) => parseDateLoose(c.mediation_date)).filter(Boolean);
      const range = fitWeekWindow(medDates, { minWeeks: 8, maxWeeks: 16 });
      const buckets = buildWeekBuckets(range, 8, 16).map((b) => ({ ...b, cases: 0, income: 0 }));
      return accumulate(buckets, (m) => {
        const dow = (m.getUTCDay() + 6) % 7;
        const start = new Date(Date.UTC(m.getUTCFullYear(), m.getUTCMonth(), m.getUTCDate() - dow));
        return `${start.getUTCFullYear()}-${pad2(start.getUTCMonth() + 1)}-${pad2(start.getUTCDate())}`;
      });
    }
    const buckets = buildMonthBuckets(chartDateRange, bucketCount, 24).map((b) => ({ ...b, cases: 0, income: 0 }));
    return accumulate(buckets, (m) => monthKey(m));
  }, [cases, dateRange, granularity, chartDateRange, bucketCount]);

  const tickStyle = { fontFamily: 'Inter Tight', fontSize: 11, fill: '#71717a' };
  const maxIncome = Math.max(0, ...data.map((d) => d.income));
  // When the entire window has zero income, let recharts auto-fit (no
  // arbitrary $1k floor). A "$0 … $1k" right axis next to an empty chart
  // implies there's something to scale to that isn't there.
  const incomeMax = maxIncome > 0 ? Math.ceil((maxIncome * 1.1) / 1000) * 1000 : 'auto';
  const totalCases = data.reduce((s, d) => s + (d.cases || 0), 0);
  const isEmpty = totalCases === 0 && maxIncome === 0;

  const handleClick = (entry) => {
    if (!entry?.activeLabel) return;
    const bucket = data.find((d) => d.label === entry.activeLabel);
    if (!bucket) return;
    if (granularity === 'weekly' && bucket.startISO) {
      onDrillDown?.({ startDate: bucket.startISO, endDate: bucket.endISO });
    } else {
      const [yStr, mStr] = bucket.key.split('-');
      const last = new Date(Date.UTC(parseInt(yStr, 10), parseInt(mStr, 10), 0)).getUTCDate();
      onDrillDown?.({ startDate: `${yStr}-${mStr}-01`, endDate: `${yStr}-${mStr}-${pad2(last)}` });
    }
  };

  return (
    <div className="bg-white rounded-[10px] border border-[#e5e5e5] flex flex-col" style={{ height: 320 }}>
      <div className="flex items-start justify-between px-4 pt-3.5 pb-3 border-b border-[#e5e5e5]">
        <div className="flex flex-col gap-0.5">
          <div className="flex items-center gap-1.5">
            <span className="text-[#0e1e20] text-sm font-semibold leading-tight" style={{ fontFamily: "'Inter Tight', sans-serif" }}>
              Month-over-Month Trend
            </span>
            <svg width="13" height="13" viewBox="0 0 16 16" fill="none">
              <circle cx="8" cy="8" r="6.5" stroke="#9ca3af" strokeWidth="1" />
              <path d="M8 7.5v3.5M8 5.25v.01" stroke="#9ca3af" strokeWidth="1.2" strokeLinecap="round" />
            </svg>
          </div>
          <span className="text-[#5b5a64] text-[11px]" style={{ fontFamily: 'Inter, sans-serif' }}>
            {subtitle || 'Cases & Revenue'}
          </span>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1.5">
            <div className="w-2.5 h-2.5 rounded-sm" style={{ background: '#2563eb' }} />
            <span className="text-[11px] text-[#5b5a64]" style={{ fontFamily: 'Inter, sans-serif' }}>Cases</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-2.5 h-2.5 rounded-sm" style={{ background: '#22c55e' }} />
            <span className="text-[11px] text-[#5b5a64]" style={{ fontFamily: 'Inter, sans-serif' }}>Revenue</span>
          </div>
          <div className="inline-flex items-center rounded-lg p-0.5" style={{ background: '#f3f4f6' }}>
            {['weekly', 'monthly'].map((g) => (
              <button
                key={g}
                type="button"
                onClick={() => setGranularity(g)}
                className="capitalize"
                style={{
                  fontFamily: 'Inter, sans-serif',
                  fontSize: 11,
                  fontWeight: 600,
                  padding: '5px 12px',
                  borderRadius: 6,
                  border: 'none',
                  cursor: 'pointer',
                  background: granularity === g ? '#fff' : 'transparent',
                  color: granularity === g ? '#0e1e20' : '#5b5a64',
                  boxShadow: granularity === g ? '0 1px 2px rgba(16,24,40,0.08)' : 'none',
                }}
              >
                {g}
              </button>
            ))}
          </div>
        </div>
      </div>
      <div className="flex-1 relative" style={{ padding: '8px 0 8px 0' }}>
        {isEmpty && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <span className="text-[12px] text-[#9ca3af]" style={{ fontFamily: 'Inter, sans-serif' }}>
              No cases or invoices in this period
            </span>
          </div>
        )}
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart
            data={data}
            margin={{ top: 8, right: 16, bottom: 4, left: 0 }}
            onClick={handleClick}
            barGap={2}
            barCategoryGap="32%"
          >
            <CartesianGrid stroke="#f3f4f6" vertical={false} />
            <XAxis dataKey="label" axisLine={false} tickLine={false} tick={tickStyle} interval={0} angle={-35} textAnchor="end" height={52} tickMargin={6} />
            <YAxis yAxisId="cases" axisLine={false} tickLine={false} tick={tickStyle} allowDecimals={false} />
            <YAxis
              yAxisId="income"
              orientation="right"
              axisLine={false}
              tickLine={false}
              tick={tickStyle}
              domain={[0, incomeMax === 'auto' ? 'auto' : incomeMax]}
              tickFormatter={fmtMoneyShort}
            />
            <ReTooltip
              cursor={{ fill: 'rgba(17,94,89,0.08)' }}
              contentStyle={{ background: '#0e1e20', border: 'none', borderRadius: 6, color: '#fff', fontFamily: 'Inter', fontSize: 11, padding: 8 }}
              labelStyle={{ color: '#fff' }}
              itemStyle={{ color: '#fff' }}
              formatter={(v, name) => (name === 'Revenue' ? fmtMoneyFull(v) : v)}
            />
            <ReBar yAxisId="cases" dataKey="cases" name="Cases" fill="#2563eb" radius={[3, 3, 0, 0]} maxBarSize={28} cursor="pointer" />
            <ReBar yAxisId="income" dataKey="income" name="Revenue" fill="#22c55e" radius={[3, 3, 0, 0]} maxBarSize={28} cursor="pointer" />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

/* ── Booking Trend (Weekly/Monthly toggle, last 8 buckets) ─────────── */
function BookingTrendChart({ cases, dateRange, onDrillDown, periodLabel, granularity: granularityProp, onGranularityChange }) {
  // Allow the parent to lift the granularity into its own state so the
  // user's weekly/monthly choice survives sibling remounts (e.g. when
  // returning from the firm-detail view). Falls back to local state for
  // callers that don't pass `granularity` / `onGranularityChange`.
  const [localGranularity, setLocalGranularity] = useState('monthly');
  const granularity = granularityProp ?? localGranularity;
  const setGranularity = onGranularityChange ?? setLocalGranularity;

  // Per-chart range — overrides any page-level date pill.
  const [chartRange, setChartRange] = useState('all');
  const chartDateRange = useMemo(() => chartRangeToDateRange(chartRange), [chartRange]);
  // Bucket count per range. Weekly: 1m≈4 wk, 3m≈13, 6m≈26, ytd up to ~52.
  // Monthly: 1m=1, 3m=3, 6m=6, ytd=Jan→now.
  const weekCount = chartRange === '1m' ? 4 : chartRange === '3m' ? 13 : chartRange === '6m' ? 26 : 52;
  const monthCount = chartRange === '1m' ? 1 : chartRange === '3m' ? 3 : chartRange === '6m' ? 6 : 12;

  const { data, truncated, totalCount, shownCount } = useMemo(() => {
    // The page-level top filter (dateRange) takes precedence: when active the
    // x-axis spans EXACTLY the selected period (daily ≤31d, else monthly), like
    // the dashboard. The weekly/monthly toggle only applies when the top filter
    // is "All time". A short per-chart window also renders daily.
    const pageRange = dateRange && (dateRange.startDate || dateRange.endDate) ? dateRange : null;
    const range = pageRange || chartDateRange;
    if (pageRange || isDailyRange(range)) {
      const daily = isDailyRange(range);
      const buckets = (daily ? buildDayBuckets(range) : buildMonthBuckets(range, monthCount, 24)).map((b) => ({ ...b, count: 0 }));
      const byKey = new Map(buckets.map((b) => [b.key, b]));
      for (const c of cases || []) {
        const d = parseDateLoose(c.created_at) || parseDateLoose(c.mediation_date);
        if (!d) continue;
        const b = byKey.get(daily ? dayKey(d) : monthKey(d));
        if (b) b.count += 1;
      }
      return { data: buckets, truncated: false, totalCount: buckets.length, shownCount: buckets.length };
    }
    if (granularity === 'weekly') {
      // Fit the weekly window to the booking dates (≤16 weeks) so the x-axis
      // stays readable, matching the Scale portal.
      const bookingDates = (cases || [])
        .map((c) => parseDateLoose(c.created_at) || parseDateLoose(c.mediation_date))
        .filter(Boolean);
      const range = fitWeekWindow(bookingDates, { minWeeks: 8, maxWeeks: 16 });
      const rawBuckets = buildWeekBuckets(range, 8, 16);
      const meta = {
        truncated: !!rawBuckets._truncated,
        totalCount: rawBuckets._totalCount || rawBuckets.length,
        shownCount: rawBuckets._shownCount || rawBuckets.length,
      };
      const buckets = rawBuckets.map((b) => ({ ...b, count: 0 }));
      const byKey = new Map(buckets.map((b) => [b.key, b]));
      for (const c of cases || []) {
        const d = parseDateLoose(c.created_at) || parseDateLoose(c.mediation_date);
        if (!d) continue;
        const dow = (d.getUTCDay() + 6) % 7;
        const start = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() - dow));
        const key = `${start.getUTCFullYear()}-${pad2(start.getUTCMonth() + 1)}-${pad2(start.getUTCDate())}`;
        const b = byKey.get(key);
        if (b) b.count += 1;
      }
      return { data: buckets, ...meta };
    }
    const rawBuckets = buildMonthBuckets(chartDateRange, monthCount, 24);
    const buckets = rawBuckets.map((b) => ({ ...b, count: 0 }));
    const byKey = new Map(buckets.map((b) => [b.key, b]));
    for (const c of cases || []) {
      const d = parseDateLoose(c.created_at) || parseDateLoose(c.mediation_date);
      if (!d) continue;
      const key = `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}`;
      const b = byKey.get(key);
      if (b) b.count += 1;
    }
    return { data: buckets, truncated: false, totalCount: buckets.length, shownCount: buckets.length };
  }, [cases, dateRange, granularity, chartDateRange, weekCount, monthCount]);

  const tickStyle = { fontFamily: 'Inter Tight', fontSize: 11, fill: '#71717a' };

  const handleClick = (entry) => {
    if (!entry?.activeLabel) return;
    const b = data.find((d) => d.label === entry.activeLabel);
    if (!b) return;
    if (granularity === 'weekly') {
      onDrillDown?.({ startDate: b.startISO, endDate: b.endISO });
    } else {
      const [yStr, mStr] = b.key.split('-');
      const last = new Date(Date.UTC(parseInt(yStr, 10), parseInt(mStr, 10), 0)).getUTCDate();
      onDrillDown?.({ startDate: `${yStr}-${mStr}-01`, endDate: `${yStr}-${mStr}-${pad2(last)}` });
    }
  };

  const windowLabel = periodLabel && periodLabel !== 'All time' ? periodLabel : null;
  const baseSubtitle = granularity === 'weekly'
    ? `Weekly bookings${windowLabel ? ` · ${windowLabel}` : ', last 8 weeks'}`
    : `Monthly bookings${windowLabel ? ` · ${windowLabel}` : ', last 6 months'}`;
  const subtitle = truncated
    ? `${baseSubtitle} · showing ${shownCount} of ${totalCount}`
    : baseSubtitle;
  const totalBookingsInWindow = data.reduce((sum, b) => sum + (b.count || 0), 0);
  const isEmpty = totalBookingsInWindow === 0;

  return (
    <div className="bg-white rounded-[10px] border border-[#e5e5e5] flex flex-col" style={{ height: 320 }}>
      <div className="flex items-start justify-between px-4 pt-3.5 pb-3 border-b border-[#e5e5e5]">
        <div className="flex flex-col gap-0.5">
          <div className="flex items-center gap-1.5">
            <span className="text-[#0e1e20] text-sm font-semibold leading-tight" style={{ fontFamily: "'Inter Tight', sans-serif" }}>
              Booking Trend
            </span>
            <svg width="13" height="13" viewBox="0 0 16 16" fill="none">
              <circle cx="8" cy="8" r="6.5" stroke="#9ca3af" strokeWidth="1" />
              <path d="M8 7.5v3.5M8 5.25v.01" stroke="#9ca3af" strokeWidth="1.2" strokeLinecap="round" />
            </svg>
          </div>
          <span className="text-[#5b5a64] text-[11px]" style={{ fontFamily: 'Inter, sans-serif' }}>{subtitle}</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex items-center rounded-lg p-0.5" style={{ background: '#f3f4f6' }}>
            {['weekly', 'monthly'].map((g) => (
              <button
                key={g}
                type="button"
                onClick={() => setGranularity(g)}
                className="capitalize"
                style={{
                  fontFamily: 'Inter, sans-serif',
                  fontSize: 11,
                  fontWeight: 600,
                  padding: '5px 12px',
                  borderRadius: 6,
                  border: 'none',
                  cursor: 'pointer',
                  background: granularity === g ? '#fff' : 'transparent',
                  color: granularity === g ? '#0e1e20' : '#5b5a64',
                  boxShadow: granularity === g ? '0 1px 2px rgba(16,24,40,0.08)' : 'none',
                }}
              >
                {g}
              </button>
            ))}
          </div>
        </div>
      </div>
      <div className="flex-1 relative" style={{ padding: '8px 0 8px 0' }}>
        {isEmpty && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <span className="text-[12px] text-[#9ca3af]" style={{ fontFamily: 'Inter, sans-serif' }}>
              No bookings in this period
            </span>
          </div>
        )}
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 24, right: 16, bottom: 4, left: 0 }} onClick={handleClick}>
            <CartesianGrid stroke="#f3f4f6" vertical={false} />
            <XAxis dataKey="label" axisLine={false} tickLine={false} tick={tickStyle} interval={0} angle={-35} textAnchor="end" height={52} tickMargin={6} />
            <YAxis axisLine={false} tickLine={false} tick={tickStyle} allowDecimals={false} />
            <ReTooltip
              cursor={{ fill: 'rgba(17,94,89,0.04)' }}
              contentStyle={{ background: '#0e1e20', border: 'none', borderRadius: 6, color: '#fff', fontFamily: 'Inter', fontSize: 11, padding: 8 }}
              labelStyle={{ color: '#fff' }}
              itemStyle={{ color: '#fff' }}
              formatter={(v) => [`${v} booking${v === 1 ? '' : 's'}`, '']}
            />
            <ReBar dataKey="count" name="Bookings" fill="#2563eb" radius={[3, 3, 0, 0]} maxBarSize={36} cursor="pointer">
              <LabelList
                dataKey="count"
                position="top"
                formatter={(v) => (v > 0 ? v : '')}
                style={{ fontFamily: 'Inter, sans-serif', fontSize: 10, fill: '#0e1e20', fontWeight: 600 }}
              />
            </ReBar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

/* ── Inquiries Trend (last 8 months, stacked converted/pending/lost) ─ */
//
// `dateRange` lets the chart filter the BE's pre-computed monthly series
// down to months that intersect the active window — so toggling the
// Date filter narrows this chart in lockstep with the others. Inquiries
// aren't case-linked in the schema so advanced filters (case/firm/side)
// can't narrow them; we show a small "All time" badge when that's the
// case to keep the user informed.
function InquiriesTrendChart({ inquiriesMonthly, advancedFiltersActive }) {
  const [chartRange, setChartRange] = useState('all');
  const chartDateRange = useMemo(() => chartRangeToDateRange(chartRange), [chartRange]);
  const data = useMemo(() => {
    const series = Array.isArray(inquiriesMonthly) ? inquiriesMonthly : [];
    const { startDate, endDate } = chartDateRange || {};
    let filtered = series;
    if (startDate || endDate) {
      // Match any month bucket that overlaps the active range. Month
      // bucket "YYYY-MM" runs from day 01 to the last day of that month.
      const startYM = startDate ? startDate.slice(0, 7) : null;
      const endYM = endDate ? endDate.slice(0, 7) : null;
      filtered = series.filter((row) => {
        if (startYM && row.key < startYM) return false;
        if (endYM && row.key > endYM) return false;
        return true;
      });
    }
    const tail = filtered.slice(-8);
    return tail.map((row) => ({
      label: MONTH_SHORT[row.month],
      converted: row.converted || 0,
      pending: row.pending || 0,
      notConverted: row.notConverted || 0,
      total: (row.converted || 0) + (row.pending || 0) + (row.notConverted || 0),
    }));
  }, [inquiriesMonthly, chartDateRange]);
  // Suppress unused-var lint — advancedFiltersActive is rendered in JSX.
  void advancedFiltersActive;

  const total = data.reduce((s, d) => s + d.total, 0);
  const converted = data.reduce((s, d) => s + d.converted, 0);
  const conversionRate = total > 0 ? Math.round((converted / total) * 100) : 0;
  const tickStyle = { fontFamily: 'Inter Tight', fontSize: 11, fill: '#71717a' };

  return (
    <div className="bg-white rounded-[10px] border border-[#e5e5e5] flex flex-col" style={{ height: 320 }}>
      <div className="flex items-start justify-between px-4 pt-3.5 pb-3 border-b border-[#e5e5e5]">
        <div className="flex flex-col gap-0.5">
          <div className="flex items-center gap-1.5">
            <span className="text-[#0e1e20] text-sm font-semibold leading-tight" style={{ fontFamily: "'Inter Tight', sans-serif" }}>
              Inquiries Trend
            </span>
            <svg width="13" height="13" viewBox="0 0 16 16" fill="none">
              <circle cx="8" cy="8" r="6.5" stroke="#9ca3af" strokeWidth="1" />
              <path d="M8 7.5v3.5M8 5.25v.01" stroke="#9ca3af" strokeWidth="1.2" strokeLinecap="round" />
            </svg>
            {advancedFiltersActive && (
              <span className="rounded-full text-[9px] font-semibold uppercase tracking-wide"
                    style={{ background: '#f3f4f6', color: '#5b5a64', padding: '2px 6px', fontFamily: 'Inter' }}
                    title="Inquiries aren't linked to cases, so case/firm/side filters can't narrow this chart.">
                Date only
              </span>
            )}
          </div>
          <span className="text-[#5b5a64] text-[11px]" style={{ fontFamily: 'Inter, sans-serif' }}>
            {total} inquiries · {conversionRate}% converted
          </span>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1.5">
            <div className="w-2.5 h-2.5 rounded-sm" style={{ background: '#115e59' }} />
            <span className="text-[11px] text-[#5b5a64]" style={{ fontFamily: 'Inter, sans-serif' }}>Converted</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-2.5 h-2.5 rounded-sm" style={{ background: '#8ac9c5' }} />
            <span className="text-[11px] text-[#5b5a64]" style={{ fontFamily: 'Inter, sans-serif' }}>Pending</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-2.5 h-2.5 rounded-sm" style={{ background: '#d7edeb' }} />
            <span className="text-[11px] text-[#5b5a64]" style={{ fontFamily: 'Inter, sans-serif' }}>Lost</span>
          </div>
          <ChartRangeDropdown value={chartRange} onChange={setChartRange} />
        </div>
      </div>
      <div className="flex-1" style={{ padding: '8px 0 8px 0' }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 16, right: 16, bottom: 4, left: 0 }} barCategoryGap="20%">
            <CartesianGrid stroke="#f3f4f6" vertical={false} />
            <XAxis dataKey="label" axisLine={false} tickLine={false} tick={tickStyle} />
            <YAxis axisLine={false} tickLine={false} tick={tickStyle} allowDecimals={false} />
            <ReTooltip
              cursor={{ fill: 'rgba(17,94,89,0.04)' }}
              contentStyle={{ background: '#0e1e20', border: 'none', borderRadius: 6, color: '#fff', fontFamily: 'Inter', fontSize: 11, padding: 8 }}
              labelStyle={{ color: '#fff' }}
              itemStyle={{ color: '#fff' }}
            />
            <ReBar dataKey="converted" name="Converted" stackId="inq" fill="#115e59" maxBarSize={28} radius={[0, 0, 0, 0]} />
            <ReBar dataKey="pending" name="Pending" stackId="inq" fill="#8ac9c5" maxBarSize={28} />
            <ReBar dataKey="notConverted" name="Lost" stackId="inq" fill="#d7edeb" maxBarSize={28} radius={[3, 3, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

/* ── Law Firms — top-N horizontal bar chart ────────────────────────── */
function LawFirmsChart({ firms }) {
  const top = (firms || []).slice(0, 6);
  // Recharts handles negative bars natively, so we encode plaintiff
  // positive / defendant negative and centered around zero — gives a
  // "tornado" comparison without two parallel charts.
  const data = useMemo(() => top.map((f) => ({
    firm: f.firm,
    plaintiff: f.plaintiff || 0,
    defendant: -(f.defendant || 0),
    total: f.total,
  })), [top]);
  const maxAbs = data.reduce((m, d) => Math.max(m, d.plaintiff, Math.abs(d.defendant)), 0) || 1;
  const tickStyle = { fontFamily: 'Inter Tight', fontSize: 11, fill: '#71717a' };

  return (
    <div className="bg-white rounded-[10px] border border-[#e5e5e5] flex flex-col" style={{ height: 320 }}>
      <div className="flex items-start justify-between px-4 pt-3.5 pb-3 border-b border-[#e5e5e5]">
        <div className="flex flex-col gap-0.5">
          <div className="flex items-center gap-1.5">
            <span className="text-[#0e1e20] text-sm font-semibold leading-tight" style={{ fontFamily: "'Inter Tight', sans-serif" }}>
              Law Firm View
            </span>
            <svg width="13" height="13" viewBox="0 0 16 16" fill="none">
              <circle cx="8" cy="8" r="6.5" stroke="#9ca3af" strokeWidth="1" />
              <path d="M8 7.5v3.5M8 5.25v.01" stroke="#9ca3af" strokeWidth="1.2" strokeLinecap="round" />
            </svg>
          </div>
          <span className="text-[#5b5a64] text-[11px]" style={{ fontFamily: 'Inter, sans-serif' }}>
            Top {data.length} firms · plaintiff vs defendant
          </span>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1.5">
            <div className="w-2.5 h-2.5 rounded-sm" style={{ background: '#115e59' }} />
            <span className="text-[11px] text-[#5b5a64]" style={{ fontFamily: 'Inter, sans-serif' }}>Plaintiff</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-2.5 h-2.5 rounded-sm" style={{ background: '#6eb2ae' }} />
            <span className="text-[11px] text-[#5b5a64]" style={{ fontFamily: 'Inter, sans-serif' }}>Defendant</span>
          </div>
        </div>
      </div>
      <div className="flex-1" style={{ padding: '8px 0 8px 0' }}>
        {data.length === 0 ? (
          <div className="h-full flex items-center justify-center">
            <span className="text-[13px] text-[#5b5a64]" style={{ fontFamily: 'Inter, sans-serif' }}>
              No law-firm data for this mediator yet.
            </span>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart layout="vertical" data={data} margin={{ top: 8, right: 16, bottom: 4, left: 8 }} stackOffset="sign">
              <CartesianGrid stroke="#f3f4f6" horizontal={false} />
              <XAxis
                type="number"
                axisLine={false}
                tickLine={false}
                tick={tickStyle}
                allowDecimals={false}
                domain={[-maxAbs, maxAbs]}
                tickFormatter={(v) => String(Math.abs(v))}
              />
              <YAxis
                dataKey="firm"
                type="category"
                axisLine={false}
                tickLine={false}
                width={120}
                // Custom tick — Recharts truncates long category labels
                // by default without a tooltip. Render a <title> child
                // so the browser shows the full domain on hover.
                tick={({ x, y, payload }) => (
                  <g transform={`translate(${x},${y})`}>
                    <text x={-6} y={0} dy={4}
                          textAnchor="end"
                          fill="#71717a"
                          style={{ fontFamily: 'Inter Tight', fontSize: 10 }}>
                      <title>{payload.value}</title>
                      {payload.value.length > 16 ? `${payload.value.slice(0, 15)}…` : payload.value}
                    </text>
                  </g>
                )}
              />
              <ReTooltip
                cursor={{ fill: 'rgba(17,94,89,0.04)' }}
                contentStyle={{ background: '#0e1e20', border: 'none', borderRadius: 6, color: '#fff', fontFamily: 'Inter', fontSize: 11, padding: 8 }}
                labelStyle={{ color: '#fff' }}
                itemStyle={{ color: '#fff' }}
                formatter={(v, name) => [Math.abs(v), name]}
              />
              {/* Plaintiff = dark teal (brand primary). Defendant = light
                  green/mint to differentiate while staying in the same
                  green family used across the Case Manager theme. */}
              <ReBar dataKey="plaintiff" name="Plaintiff" stackId="firm" fill="#115e59" maxBarSize={18} radius={[0, 3, 3, 0]} />
              <ReBar dataKey="defendant" name="Defendant" stackId="firm" fill="#6eb2ae" maxBarSize={18} radius={[3, 0, 0, 3]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}

/* ── Law Firms — Plaintiff vs Defendant Breakdown ──────────────────── */
function LawFirmsTable({ firms, selectedFirm, onSelect }) {
  const rows = firms || [];
  const [showAll, setShowAll] = useState(false);
  const visible = showAll ? rows : rows.slice(0, 10);
  const hasMore = rows.length > 10;
  return (
    <div className="bg-white rounded-[10px] border border-[#e5e5e5]">
      <div className="flex items-start justify-between px-4 pt-3.5 pb-3 border-b border-[#e5e5e5]">
        <div className="flex flex-col gap-0.5">
          <div className="flex items-center gap-1.5">
            <span className="text-[#0e1e20] text-sm font-semibold leading-tight" style={{ fontFamily: "'Inter Tight', sans-serif" }}>
              Law Firms — Plaintiff vs Defendant Breakdown ({rows.length})
            </span>
            <svg width="13" height="13" viewBox="0 0 16 16" fill="none">
              <circle cx="8" cy="8" r="6.5" stroke="#9ca3af" strokeWidth="1" />
              <path d="M8 7.5v3.5M8 5.25v.01" stroke="#9ca3af" strokeWidth="1.2" strokeLinecap="round" />
            </svg>
          </div>
          <span className="text-[#5b5a64] text-[11px]" style={{ fontFamily: 'Inter, sans-serif' }}>
            Firms working with this mediator across the selected window
          </span>
        </div>
      </div>
      <div className="overflow-hidden">
        <div className="grid items-center px-4 py-2.5 bg-[#f9fafb] border-b border-[#f0f0f0]"
             style={{ gridTemplateColumns: '40px 1fr 100px 100px 100px 120px' }}>
          <span className="text-[#5b5a64] text-[11px] font-medium" style={{ fontFamily: 'Inter, sans-serif' }}>#</span>
          <span className="text-[#5b5a64] text-[11px] font-medium" style={{ fontFamily: 'Inter, sans-serif' }}>Firm</span>
          <span className="text-[#5b5a64] text-[11px] font-medium text-right" style={{ fontFamily: 'Inter, sans-serif' }}>Total</span>
          <span className="text-[#5b5a64] text-[11px] font-medium text-right" style={{ fontFamily: 'Inter, sans-serif' }}>Plaintiff</span>
          <span className="text-[#5b5a64] text-[11px] font-medium text-right" style={{ fontFamily: 'Inter, sans-serif' }}>Defendant</span>
          <span className="text-[#5b5a64] text-[11px] font-medium text-right" style={{ fontFamily: 'Inter, sans-serif' }}>Income</span>
        </div>
        {rows.length === 0 && (
          <div className="px-4 py-6 text-center">
            <span className="text-[13px] text-[#5b5a64]" style={{ fontFamily: 'Inter, sans-serif' }}>
              No law firms tracked for this mediator yet.
            </span>
          </div>
        )}
        {visible.map((f) => {
          const isSelected = selectedFirm && selectedFirm.firm === f.firm;
          return (
            <div
              key={f.firm}
              role={onSelect ? 'button' : undefined}
              tabIndex={onSelect ? 0 : undefined}
              onClick={() => onSelect && onSelect(f)}
              onKeyDown={(e) => {
                if (!onSelect) return;
                if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect(f); }
              }}
              className={`grid items-center px-4 py-2.5 border-b border-[#f0f0f0] last:border-b-0 ${onSelect ? 'cursor-pointer hover:bg-[#f9fafb]' : ''} ${isSelected ? 'bg-[#f0fdfa]' : ''}`}
              style={{ gridTemplateColumns: '40px 1fr 100px 100px 100px 120px' }}
            >
              <span className="text-[#5b5a64] text-[12px]" style={{ fontFamily: 'Inter, sans-serif' }}>{f.rank}</span>
              <div className="flex items-center gap-2 min-w-0">
                <span className="text-[#115e59] text-[13px] underline decoration-[#b7ebdf] underline-offset-2 truncate"
                      style={{ fontFamily: 'Inter, sans-serif' }}
                      title={f.lastCaseName ? `Last booking: ${f.lastCaseName}${f.lastCaseDate ? ` · ${f.lastCaseDate}` : ''}` : f.firm}>
                  {f.firm}
                </span>
                {isSelected && (
                  <span className="rounded-full px-2 py-[1px] text-[9px] font-bold uppercase tracking-wide flex-shrink-0"
                        style={{ background: '#115e59', color: '#fff', fontFamily: 'Inter, sans-serif' }}>
                    Selected
                  </span>
                )}
              </div>
              <span className="text-[#0e1e20] text-[13px] text-right font-medium tabular-nums" style={{ fontFamily: 'Inter, sans-serif' }}>{f.total}</span>
              <span className="text-[#0e1e20] text-[13px] text-right tabular-nums" style={{ fontFamily: 'Inter, sans-serif' }}>{f.plaintiff > 0 ? f.plaintiff : '—'}</span>
              <span className="text-[#0e1e20] text-[13px] text-right tabular-nums" style={{ fontFamily: 'Inter, sans-serif' }}>{f.defendant > 0 ? f.defendant : '—'}</span>
              <span className="text-[#0e1e20] text-[13px] text-right tabular-nums" style={{ fontFamily: 'Inter, sans-serif' }}>{f.income > 0 ? fmtMoneyFull(f.income) : '—'}</span>
            </div>
          );
        })}
        {hasMore && (
          <div className="flex items-center justify-center px-4 py-2.5 border-t border-[#f0f0f0] bg-[#fafafa]">
            <button
              type="button"
              onClick={() => setShowAll((v) => !v)}
              className="text-[#115e59] text-[12px] font-semibold hover:underline"
              style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontFamily: 'Inter, sans-serif' }}
            >
              {showAll ? `Show top 10` : `Show all ${rows.length} firms`}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/* ── AI Mediator Insights placeholder card ─────────────────────────── */
//
/* ── Firm Detail Drawer (right-side slide-over) ────────────────────── */
//
// Opens when a Law Firms breakdown row is clicked. Filters the mediator's
// cases by matching the firm's domain against plaintiff/defendant client
// emails. Derives a per-case status badge from the traffic-light colors
// + case.status field (so "Closed" reflects the actual case lifecycle,
// "On track" / "At risk" reflect onboarding+payment progress).
function FirmDetailDrawer({ firm, cases, mediatorId, side = 'all', onClose, onViewProfile }) {
  // Body scroll lock + Escape-to-close while open.
  useEffect(() => {
    if (!firm) return undefined;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      document.removeEventListener('keydown', onKey);
    };
  }, [firm, onClose]);

  if (!firm) return null;

  const domain = firm.firm;
  // PERSONAL_EMAIL_DOMAINS is imported from the shared util — same
  // literal list as the BE's `server/utils/personalEmailDomains.js`,
  // so the drawer's matcher stays in sync with the BE aggregation.
  const isPersonalBucket = domain === PERSONAL_EMAIL_BUCKET_LABEL;
  const matches = (email) => {
    if (!email || typeof email !== 'string') return false;
    const at = email.lastIndexOf('@');
    if (at < 0) return false;
    const d = email.slice(at + 1).trim().toLowerCase();
    if (isPersonalBucket) return PERSONAL_EMAIL_DOMAINS.has(d);
    return d === String(domain).toLowerCase();
  };

  // Cases linked to this firm. Honour the active Side filter — when
  // side='plaintiff', only count cases where this firm represents the
  // plaintiff side (and vice versa for defendant).
  const firmCases = (cases || []).filter((c) => {
    const onPlaintiff = matches(c.plaintiffEmail);
    const onDefendant = matches(c.defendantEmail);
    if (side === 'plaintiff') return onPlaintiff;
    if (side === 'defendant') return onDefendant;
    return onPlaintiff || onDefendant;
  });

  // Recent: most-recent mediation_date (or created_at) first.
  const recent = [...firmCases].sort((a, b) => {
    const da = a.mediation_date || a.created_at || '';
    const db = b.mediation_date || b.created_at || '';
    return db.localeCompare(da);
  }).slice(0, 5);

  // Friendly title — Title Case the first DNS label and only append
  // "Law" when the domain itself signals a law firm. Avoids weird
  // outputs like "Statecourt Law" for statecourt.gov.
  const titleFromDomain = (() => {
    if (!domain || domain === 'Personal email') return domain || '—';
    const head = String(domain).split('.')[0] || String(domain);
    const looksLikeLaw = /law|llp|pllc|attorneys?/i.test(domain) || /law|llp|pllc|attorneys?/i.test(head);
    const stripped = head.replace(/[-_]+/g, ' ').replace(/\b(law|llp|pllc|pc|inc|corp|attorneys?)\b/gi, '').trim();
    const base = (stripped || head).replace(/\w\S*/g, (w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase());
    return looksLikeLaw && !/\blaw\b/i.test(base) ? `${base} Law` : base;
  })();

  const statusForCase = (c) => {
    // BE filters out not_active/cancelled and returns active|completed.
    if (c.status === 'completed') return { label: 'Closed', bg: '#f3f4f6', color: '#5b5a64' };
    // Scope the "At risk" check to the active side. Looking at the
    // plaintiff view of a firm shouldn't flag red on the defendant's
    // unrelated brief — that signal isn't actionable from this view.
    const sourceList = side === 'plaintiff'
      ? (c.plaintiff || [])
      : side === 'defendant'
        ? (c.defendant || [])
        : [...(c.plaintiff || []), ...(c.defendant || [])];
    const colors = sourceList.map((s) => s.color);
    if (colors.includes('red')) return { label: 'At risk', bg: '#fef3c7', color: '#b45309' };
    return { label: 'On track', bg: '#e8fdf5', color: '#00a666' };
  };

  const roleForCase = (c) => {
    // When a side filter is active, role is fixed. Otherwise infer
    // from which side the firm actually matched on this case.
    if (side === 'plaintiff') return 'P';
    if (side === 'defendant') return 'D';
    return matches(c.plaintiffEmail) ? 'P' : 'D';
  };

  return (
    <>
      <div
        onClick={onClose}
        className="fixed inset-0 z-40"
        style={{ background: 'rgba(15, 23, 42, 0.35)' }}
      />
      <aside
        className="fixed top-0 right-0 z-50 bg-white flex flex-col shadow-2xl"
        style={{ width: 380, height: '100vh', borderLeft: '1px solid #e5e5e5' }}
      >
        {/* Header */}
        <div className="flex items-start justify-between px-5 py-4 border-b border-[#e5e5e5]">
          <div className="flex flex-col gap-1">
            <span className="text-[#0e1e20] text-base font-semibold" style={{ fontFamily: "'Inter Tight', sans-serif" }}>
              {titleFromDomain}
            </span>
            {domain === PERSONAL_EMAIL_BUCKET_LABEL ? (
              // "Personal email" is a synthetic bucket, not a real
              // domain — rendering the globe link would produce an
              // invalid URL like https://Personal email. Show the
              // label as plain text instead.
              <span
                className="flex items-center gap-1 text-[#5b5a64] text-[12px]"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                {domain}
              </span>
            ) : (
              <a
                href={`https://${domain}`}
                target="_blank"
                rel="noreferrer noopener"
                className="flex items-center gap-1 text-[#115e59] text-[12px] hover:underline"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                <svg width="11" height="11" viewBox="0 0 12 12" fill="none">
                  <circle cx="6" cy="6" r="4.5" stroke="currentColor" strokeWidth="1" />
                  <path d="M1.5 6h9M6 1.5c1.4 1.2 2.2 2.8 2.2 4.5S7.4 9.3 6 10.5M6 1.5C4.6 2.7 3.8 4.3 3.8 6S4.6 9.3 6 10.5" stroke="currentColor" strokeWidth="0.8" />
                </svg>
                {domain}
                <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
                  <path d="M5 2h5v5M10 2L5 7M5 4H3a1 1 0 00-1 1v4a1 1 0 001 1h4a1 1 0 001-1V7" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </a>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="text-[#9ca3af] hover:text-[#0e1e20]"
            style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 4, lineHeight: 0 }}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {/* KPI grid */}
          <div className="grid grid-cols-2 gap-2.5 mb-5">
            {[
              { label: 'TOTAL CASES', value: firm.total },
              { label: 'INCOME', value: firm.income > 0 ? fmtMoneyFull(firm.income) : '—' },
              { label: 'PLAINTIFF', value: firm.plaintiff || 0 },
              { label: 'DEFENDANT', value: firm.defendant || 0 },
            ].map((k) => (
              <div key={k.label} className="rounded-[8px] border border-[#e5e5e5] bg-white px-3 py-2.5 flex flex-col gap-1">
                <span className="text-[#9ca3af] text-[10px] font-semibold tracking-wide" style={{ fontFamily: 'Inter, sans-serif' }}>
                  {k.label}
                </span>
                <span className="text-[#0e1e20] text-[18px] font-semibold leading-tight" style={{ fontFamily: "'Inter Tight', sans-serif" }}>
                  {k.value}
                </span>
              </div>
            ))}
          </div>

          {/* Recent cases */}
          <div className="flex items-center justify-between mb-2">
            <span className="text-[#0e1e20] text-[13px] font-semibold" style={{ fontFamily: "'Inter Tight', sans-serif" }}>
              Recent cases
            </span>
            <span className="text-[#9ca3af] text-[11px]" style={{ fontFamily: 'Inter, sans-serif' }}>
              {recent.length} of {firmCases.length}
            </span>
          </div>
          <div className="flex flex-col">
            {recent.length === 0 && (
              <div className="py-6 text-center">
                <span className="text-[12px] text-[#5b5a64]" style={{ fontFamily: 'Inter, sans-serif' }}>
                  No cases linked to this firm.
                </span>
              </div>
            )}
            {recent.map((c) => {
              const st = statusForCase(c);
              const role = roleForCase(c);
              const when = c.dateRaw || c.mediation_date || c.created_at;
              // Zone-free business DATE → literal; timestamptz → Pacific.
              const dateLabel = when
                ? (String(when).length === 10
                    ? (() => { const [yy, mm, dd] = String(when).split('-').map(Number); return new Date(yy, mm - 1, dd).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }); })()
                    : new Date(when).toLocaleDateString('en-US', { timeZone: 'America/Los_Angeles', month: 'short', day: 'numeric' }))
                : '';
              return (
                <div
                  key={c.id}
                  className="flex items-center gap-2 py-2.5 border-b border-[#f0f0f0] last:border-b-0"
                >
                  <div className="rounded-full flex items-center justify-center flex-shrink-0"
                       style={{ width: 24, height: 24, background: role === 'P' ? '#e8fdf5' : '#fef3c7' }}>
                    <span className="text-[10px] font-bold"
                          style={{ color: role === 'P' ? '#115e59' : '#b45309', fontFamily: 'Inter, sans-serif' }}>
                      {role}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[#0e1e20] text-[12px] font-medium leading-tight truncate"
                         style={{ fontFamily: 'Inter, sans-serif' }}
                         title={c.name}>
                      {c.name}
                    </div>
                    <div className="text-[#9ca3af] text-[10px]" style={{ fontFamily: 'Inter, sans-serif' }}>
                      {c.caseNumber || `Case ${c.id}`}{dateLabel ? ` · ${dateLabel}` : ''}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-[#0e1e20] text-[12px] font-semibold tabular-nums" style={{ fontFamily: 'Inter, sans-serif' }}>
                      {Number(c.revenueInvoicedGrossByDomain?.[domain] ?? 0) > 0 ? fmtMoneyFull(Number(c.revenueInvoicedGrossByDomain?.[domain] ?? 0)) : '—'}
                    </div>
                  </div>
                  <span
                    className="rounded-full px-2 py-[2px] text-[10px] font-semibold"
                    style={{ background: st.bg, color: st.color, fontFamily: 'Inter, sans-serif' }}
                  >
                    {st.label}
                  </span>
                </div>
              );
            })}
          </div>
          {firmCases.length > recent.length && (
            <a
              href={`/cases?firmDomain=${encodeURIComponent(domain)}`}
              className="inline-flex items-center gap-1 mt-3 text-[#115e59] text-[12px] font-medium hover:underline"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              View all {firmCases.length} cases for this firm
              <svg width="11" height="11" viewBox="0 0 12 12" fill="none">
                <path d="M2 6h8M6 2l4 4-4 4" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </a>
          )}
        </div>

        {/* Footer actions */}
        <div className="grid grid-cols-2 gap-2 px-5 py-3 border-t border-[#e5e5e5] bg-white">
          {(() => {
            // Resolve the first matching email up front so the button can
            // disable itself when there's nothing to mail. Clicking a
            // dead button (silent no-op) is worse than no button at all.
            const primaryEmail = firmCases.find((c) => matches(c.plaintiffEmail))?.plaintiffEmail
              || firmCases.find((c) => matches(c.defendantEmail))?.defendantEmail
              || null;
            const disabled = !primaryEmail;
            return (
              <button
                type="button"
                disabled={disabled}
                onClick={() => {
                  if (primaryEmail) window.location.href = `mailto:${primaryEmail}`;
                }}
                title={disabled ? 'No email on file for this firm’s cases' : `Email ${primaryEmail}`}
                className="rounded-lg border border-[#e5e5e5] bg-white hover:bg-[#f9fafb]"
                style={{
                  padding: '8px 12px',
                  fontFamily: 'Inter, sans-serif',
                  fontSize: 12,
                  fontWeight: 600,
                  color: disabled ? '#9ca3af' : '#0e1e20',
                  cursor: disabled ? 'not-allowed' : 'pointer',
                  opacity: disabled ? 0.6 : 1,
                }}
              >
                Email primary contact
              </button>
            );
          })()}
          <button
            type="button"
            onClick={() => onViewProfile && onViewProfile(domain)}
            className="rounded-lg"
            style={{ padding: '8px 12px', background: '#115e59', color: '#fff', border: 'none', fontFamily: 'Inter, sans-serif', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
          >
            View firm profile
          </button>
        </div>
      </aside>
    </>
  );
}

/* ── Firm Detail View — inline (replaces the drawer) ───────────────── */
//
// Shown in the page body when a row in the Law Firms breakdown is
// clicked. Renders a header (Back button + firm name + total count +
// total income) and a per-case table (Booked / Mediation / Case Name /
// Attorney(s) at <domain> / Income). Honours the active `side` filter
// so plaintiff/defendant scoping carries through. Pure presentational
// view — `onBack` clears the openFirm state, `onCaseClick` navigates
// to the case detail.
function FirmDetailView({ firm, cases, side = 'all', onBack, onCaseClick, onAttorneyClick }) {
  if (!firm) return null;
  const domain = firm.firm;

  const PERSONAL = PERSONAL_EMAIL_DOMAINS;
  const isPersonalBucket = domain === PERSONAL_EMAIL_BUCKET_LABEL;
  const matches = (email) => {
    if (!email || typeof email !== 'string') return false;
    const at = email.lastIndexOf('@');
    if (at < 0) return false;
    const d = email.slice(at + 1).trim().toLowerCase();
    if (isPersonalBucket) return PERSONAL.has(d);
    return d === String(domain).toLowerCase();
  };

  const firmCases = (cases || [])
    .filter((c) => {
      const p = matches(c.plaintiffEmail);
      const d = matches(c.defendantEmail);
      if (side === 'plaintiff') return p;
      if (side === 'defendant') return d;
      return p || d;
    })
    .sort((a, b) => {
      const da = a.created_at || '';
      const db = b.created_at || '';
      return db.localeCompare(da);
    });

  const totalIncome = firmCases.reduce((s, c) => s + (Number(c.revenueInvoicedGrossByDomain?.[domain] ?? 0) || 0), 0);

  const fmtDate = (raw) => {
    if (!raw) return '—';
    // Zone-free business DATE (length 10) → render literally; timestamptz →
    // project to Pacific (matches Scale) so the day doesn't shift per viewer.
    if (String(raw).length === 10) {
      const [y, m, d] = String(raw).split('-').map(Number);
      return new Date(y, m - 1, d).toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' });
    }
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleDateString('en-US', { timeZone: 'America/Los_Angeles', year: 'numeric', month: '2-digit', day: '2-digit' });
  };
  const attorneyEmail = (c) => {
    if (matches(c.plaintiffEmail)) return c.plaintiffEmail || '';
    if (matches(c.defendantEmail)) return c.defendantEmail || '';
    return '';
  };

  return (
    <div className="bg-white rounded-[10px] border border-[#e5e5e5] overflow-hidden">
      {/* Header — Back button + firm summary */}
      <div className="flex items-center gap-4 px-4 py-3.5 border-b border-[#e5e5e5]">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-2 rounded-lg border border-[#e5e5e5] bg-white hover:bg-[#f9fafb]"
          style={{ padding: '6px 12px', fontFamily: 'Inter, sans-serif', fontSize: 13, fontWeight: 500, color: '#0e1e20', cursor: 'pointer' }}
        >
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M9 3L5 7l4 4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          Back to Firms
        </button>
        <span className="text-[#0e1e20] text-base font-semibold leading-tight" style={{ fontFamily: "'Inter Tight', sans-serif" }}>
          {domain} — {firmCases.length} {firmCases.length === 1 ? 'case' : 'cases'} ({fmtMoneyFull(totalIncome)} total income)
        </span>
      </div>

      {/* Table header */}
      <div className="grid items-center px-4 py-2.5 bg-[#f9fafb] border-b border-[#f0f0f0]"
           style={{ gridTemplateColumns: '120px 120px 1fr 1fr 120px' }}>
        <span className="text-[#5b5a64] text-[12px] font-semibold" style={{ fontFamily: 'Inter, sans-serif' }}>Booked</span>
        <span className="text-[#5b5a64] text-[12px] font-semibold" style={{ fontFamily: 'Inter, sans-serif' }}>Mediation</span>
        <span className="text-[#5b5a64] text-[12px] font-semibold" style={{ fontFamily: 'Inter, sans-serif' }}>Case Name</span>
        <span className="text-[#5b5a64] text-[12px] font-semibold" style={{ fontFamily: 'Inter, sans-serif' }}>Attorney(s) at {domain}</span>
        <span className="text-[#5b5a64] text-[12px] font-semibold text-right" style={{ fontFamily: 'Inter, sans-serif' }}>Income</span>
      </div>

      {/* Data rows */}
      {firmCases.length === 0 && (
        <div className="px-4 py-6 text-center text-[13px] text-[#5b5a64]" style={{ fontFamily: 'Inter, sans-serif' }}>
          No cases linked to {domain} {side !== 'all' ? `on the ${side} side` : ''}.
        </div>
      )}
      {firmCases.map((c) => (
        <div
          key={c.id}
          role="button"
          tabIndex={0}
          onClick={() => onCaseClick && onCaseClick(c.id)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onCaseClick && onCaseClick(c.id); }
          }}
          className="grid items-center px-4 py-2.5 border-b border-[#f0f0f0] last:border-b-0 hover:bg-[#f9fafb] cursor-pointer focus:outline-none focus:bg-[#f3f4f6]"
          style={{ gridTemplateColumns: '120px 120px 1fr 1fr 120px' }}
        >
          <span className="text-[#0e1e20] text-[13px] tabular-nums" style={{ fontFamily: 'Inter, sans-serif' }}>
            {fmtDate(c.created_at)}
          </span>
          <span className="text-[#0e1e20] text-[13px] tabular-nums" style={{ fontFamily: 'Inter, sans-serif' }}>
            {fmtDate(c.mediation_date)}
          </span>
          <span className="text-[#115e59] text-[13px] underline decoration-[#b7ebdf] underline-offset-2 truncate"
                style={{ fontFamily: 'Inter, sans-serif' }}
                title={c.name}>
            {c.name || '(unnamed case)'}
          </span>
          {attorneyEmail(c) && onAttorneyClick ? (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onAttorneyClick(attorneyEmail(c)); }}
              className="text-[#115e59] text-[13px] underline decoration-[#b7ebdf] underline-offset-2 truncate text-left hover:text-[#0d4a47]"
              style={{ fontFamily: 'Inter, sans-serif', background: 'transparent', border: 'none', padding: 0, cursor: 'pointer' }}
              title={`View all cases for ${attorneyEmail(c)}`}
            >
              {attorneyEmail(c)}
            </button>
          ) : (
            <span className="text-[#0e1e20] text-[13px] truncate"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                  title={attorneyEmail(c)}>
              {attorneyEmail(c) || '—'}
            </span>
          )}
          <span className="text-[#0e1e20] text-[13px] font-semibold tabular-nums text-right"
                style={{ fontFamily: 'Inter, sans-serif' }}>
            {Number(c.revenueInvoicedGrossByDomain?.[domain] ?? 0) > 0 ? fmtMoneyFull(Number(c.revenueInvoicedGrossByDomain?.[domain] ?? 0)) : '—'}
          </span>
        </div>
      ))}

      {/* Attorney rollup at this firm — per the 2026-05-21 standup with
          Adam. "Another table by the attorney at that firm" so the user
          can see at a glance which attorneys are giving the mediator the
          most cases. Sorted by case count DESC ("which attorney at the
          firm is giving me the most work"), tie-break on revenue DESC.
          Rows clickable → opens AttorneyDetailView for that email. */}
      {(() => {
        const byEmail = new Map();
        for (const c of firmCases) {
          const email = attorneyEmail(c);
          if (!email) continue;
          const key = email.toLowerCase();
          const agg = byEmail.get(key) || { email, cases: 0, income: 0 };
          agg.cases += 1;
          agg.income += Number(c.revenueInvoicedGrossByDomain?.[domain] ?? 0) || 0;
          byEmail.set(key, agg);
        }
        const rollup = Array.from(byEmail.values()).sort(
          (a, b) => (b.cases - a.cases) || (b.income - a.income),
        );
        if (rollup.length === 0) return null;
        return (
          <div className="border-t-2 border-[#e5e5e5]">
            <div className="flex items-baseline gap-2 px-4 py-3.5 border-b border-[#e5e5e5]">
              <span className="text-[#0e1e20] text-sm font-semibold leading-tight" style={{ fontFamily: "'Inter Tight', sans-serif" }}>
                Attorneys at {domain} ({rollup.length})
              </span>
              <span className="text-[#5b5a64] text-[11px]" style={{ fontFamily: 'Inter, sans-serif' }}>
                Sorted by case count — top attorneys giving this mediator the most work
              </span>
            </div>
            <div className="grid items-center px-4 py-2.5 bg-[#f9fafb] border-b border-[#f0f0f0]"
                 style={{ gridTemplateColumns: '48px 1fr 110px 140px' }}>
              <span className="text-[#5b5a64] text-[12px] font-semibold" style={{ fontFamily: 'Inter, sans-serif' }}>#</span>
              <span className="text-[#5b5a64] text-[12px] font-semibold" style={{ fontFamily: 'Inter, sans-serif' }}>Attorney (email)</span>
              <span className="text-[#5b5a64] text-[12px] font-semibold text-right" style={{ fontFamily: 'Inter, sans-serif' }}># Cases</span>
              <span className="text-[#5b5a64] text-[12px] font-semibold text-right" style={{ fontFamily: 'Inter, sans-serif' }}>Total Income</span>
            </div>
            {rollup.map((r, idx) => {
              const RowTag = onAttorneyClick ? 'button' : 'div';
              const handleClick = onAttorneyClick ? () => onAttorneyClick(r.email) : undefined;
              return (
                <RowTag
                  key={r.email}
                  type={onAttorneyClick ? 'button' : undefined}
                  onClick={handleClick}
                  className={`grid items-center px-4 py-2.5 border-b border-[#f0f0f0] last:border-b-0 w-full text-left ${onAttorneyClick ? 'hover:bg-[#f9fafb] cursor-pointer' : ''}`}
                  style={{ gridTemplateColumns: '48px 1fr 110px 140px', background: 'transparent', border: 'none', borderBottom: '1px solid #f0f0f0' }}
                >
                  <span className="text-[#9ca3af] text-[12px] tabular-nums" style={{ fontFamily: 'Inter, sans-serif' }}>{idx + 1}</span>
                  <span
                    className={`text-[13px] truncate ${onAttorneyClick ? 'text-[#115e59] underline decoration-[#b7ebdf] underline-offset-2' : 'text-[#0e1e20]'}`}
                    style={{ fontFamily: 'Inter, sans-serif' }}
                    title={r.email}
                  >
                    {r.email}
                  </span>
                  <span className="text-[#0e1e20] text-[13px] tabular-nums text-right" style={{ fontFamily: 'Inter, sans-serif' }}>
                    {r.cases}
                  </span>
                  <span className="text-[#0e1e20] text-[13px] font-semibold tabular-nums text-right" style={{ fontFamily: 'Inter, sans-serif' }}>
                    {r.income > 0 ? fmtMoneyFull(r.income) : '—'}
                  </span>
                </RowTag>
              );
            })}
          </div>
        );
      })()}
    </div>
  );
}

/* ── Attorney Detail (drilled in from FirmDetailView's Attorney column) ──
 *
 * Header `<email> — Cases (N)` + Private / MCLA pills + Cases / Total
 * Admin Fee / Total Income stats + per-case table.
 *
 * Attorney = a plaintiff or defendant email address on a case. We filter
 * `cases` to ones where either side's email matches the chosen attorney,
 * then derive everything from that subset on the FE.
 */
function AttorneyDetailView({ email, cases, onBack, onCaseClick, domainOf }) {
  if (!email) return null;
  const normalized = String(email).trim().toLowerCase();
  // The attorney belongs to ONE firm — derive its domain from the email so
  // per-case Income shows this firm's OWN invoiced share, not the full case
  // total. Prefer the shared domainOf (collapses personal domains to the
  // "Personal email" bucket); fall back to a local derivation if absent.
  const PERSONAL = PERSONAL_EMAIL_DOMAINS;
  const firmDomain = domainOf
    ? domainOf(email)
    : (() => {
        const at = normalized.lastIndexOf('@');
        if (at < 0) return null;
        const d = normalized.slice(at + 1).trim().toLowerCase();
        if (!d) return null;
        return PERSONAL.has(d) ? PERSONAL_EMAIL_BUCKET_LABEL : d;
      })();
  const incomeForFirm = (c) => Number(c.revenueInvoicedGrossByDomain?.[firmDomain] ?? 0) || 0;
  const attorneyCases = (cases || [])
    .filter((c) => {
      const p = (c.plaintiffEmail || '').trim().toLowerCase();
      const d = (c.defendantEmail || '').trim().toLowerCase();
      return p === normalized || d === normalized;
    })
    .sort((a, b) => {
      const da = a.created_at || '';
      const db = b.created_at || '';
      return db.localeCompare(da);
    });

  // Pills: Private = neither is_gsc_case nor is_car_case; MCLA =
  // is_gsc_case. Pro Bono is intentionally omitted — no schema field
  // tracks it yet.
  const mclaCount = attorneyCases.filter((c) => c.is_gsc_case).length;
  const privateCount = attorneyCases.length - mclaCount;
  const totalAdminFee = attorneyCases.reduce((s, c) => s + (Number(c.admin_fee) || 0), 0);
  const totalCmFee = attorneyCases.reduce((s, c) => s + (Number(c.case_manager_fee_amount) || 0), 0);
  const totalIncome = attorneyCases.reduce((s, c) => s + incomeForFirm(c), 0);

  const fmtDate = (raw) => {
    if (!raw) return '—';
    // Zone-free business DATE (length 10) → render literally; timestamptz →
    // project to Pacific (matches Scale) so the day doesn't shift per viewer.
    if (String(raw).length === 10) {
      const [y, m, d] = String(raw).split('-').map(Number);
      return new Date(y, m - 1, d).toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' });
    }
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleDateString('en-US', { timeZone: 'America/Los_Angeles', year: 'numeric', month: '2-digit', day: '2-digit' });
  };

  return (
    <div className="bg-white rounded-[10px] border border-[#e5e5e5] overflow-hidden">
      {/* Header — Back button + email + cases count + case-type pills */}
      <div className="flex items-center gap-4 px-4 py-3.5 border-b border-[#e5e5e5] flex-wrap">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-2 rounded-lg border border-[#e5e5e5] bg-white hover:bg-[#f9fafb]"
          style={{ padding: '6px 12px', fontFamily: 'Inter, sans-serif', fontSize: 13, fontWeight: 500, color: '#0e1e20', cursor: 'pointer' }}
        >
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M9 3L5 7l4 4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          Back
        </button>
        <span className="text-[#0e1e20] text-base font-semibold leading-tight flex-1 truncate" style={{ fontFamily: "'Inter Tight', sans-serif" }} title={email}>
          {email} — Cases ({attorneyCases.length})
        </span>
        <div className="flex items-center gap-2">
          <span className="rounded-full text-[12px] font-semibold"
                style={{ background: '#2563eb', color: '#fff', padding: '5px 14px', fontFamily: 'Inter, sans-serif' }}>
            Private ({privateCount})
          </span>
          <span className="rounded-full text-[12px] font-semibold"
                style={{ background: '#2563eb', color: '#fff', padding: '5px 14px', fontFamily: 'Inter, sans-serif' }}>
            MCLA ({mclaCount})
          </span>
        </div>
      </div>

      {/* Stats row */}
      <div className="flex items-center gap-6 px-4 py-3 bg-[#f9fafb] border-b border-[#f0f0f0]">
        <div className="flex items-center gap-1.5">
          <span className="text-[12px] text-[#5b5a64]" style={{ fontFamily: 'Inter, sans-serif' }}>Cases:</span>
          <span className="text-[13px] font-semibold text-[#0e1e20] tabular-nums" style={{ fontFamily: 'Inter, sans-serif' }}>{attorneyCases.length}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="text-[12px] text-[#5b5a64]" style={{ fontFamily: 'Inter, sans-serif' }}>Total Mediation Fee:</span>
          <span className="text-[13px] font-semibold text-[#0e1e20] tabular-nums" style={{ fontFamily: 'Inter, sans-serif' }}>{fmtMoneyFull(totalAdminFee)}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="text-[12px] text-[#5b5a64]" style={{ fontFamily: 'Inter, sans-serif' }}>Total CM Fee:</span>
          <span className="text-[13px] font-semibold text-[#0e1e20] tabular-nums" style={{ fontFamily: 'Inter, sans-serif' }}>{fmtMoneyFull(totalCmFee)}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="text-[12px] text-[#5b5a64]" style={{ fontFamily: 'Inter, sans-serif' }}>Total Income:</span>
          <span className="text-[13px] font-semibold text-[#0e1e20] tabular-nums" style={{ fontFamily: 'Inter, sans-serif' }}>{fmtMoneyFull(totalIncome)}</span>
        </div>
      </div>

      {/* Table header */}
      <div className="grid items-center px-4 py-2.5 bg-[#f9fafb] border-b border-[#f0f0f0]"
           style={{ gridTemplateColumns: '110px 1fr 110px 110px 110px 1fr 1fr' }}>
        <span className="text-[#5b5a64] text-[12px] font-semibold" style={{ fontFamily: 'Inter, sans-serif' }}>Date</span>
        <span className="text-[#5b5a64] text-[12px] font-semibold" style={{ fontFamily: 'Inter, sans-serif' }}>Case Name</span>
        <span className="text-[#5b5a64] text-[12px] font-semibold text-right" style={{ fontFamily: 'Inter, sans-serif' }}>Mediation Fee</span>
        <span className="text-[#5b5a64] text-[12px] font-semibold text-right" style={{ fontFamily: 'Inter, sans-serif' }}>CM Fee</span>
        <span className="text-[#5b5a64] text-[12px] font-semibold text-right" style={{ fontFamily: 'Inter, sans-serif' }}>Income</span>
        <span className="text-[#5b5a64] text-[12px] font-semibold" style={{ fontFamily: 'Inter, sans-serif' }}>Plaintiff Law Firm</span>
        <span className="text-[#5b5a64] text-[12px] font-semibold" style={{ fontFamily: 'Inter, sans-serif' }}>Defendant Law Firm</span>
      </div>

      {/* Rows */}
      {attorneyCases.length === 0 && (
        <div className="px-4 py-6 text-center text-[13px] text-[#5b5a64]" style={{ fontFamily: 'Inter, sans-serif' }}>
          No cases linked to this attorney.
        </div>
      )}
      {attorneyCases.map((c) => (
        <div
          key={c.id}
          role="button"
          tabIndex={0}
          onClick={() => onCaseClick && onCaseClick(c.id)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onCaseClick && onCaseClick(c.id); }
          }}
          className="grid items-center px-4 py-2.5 border-b border-[#f0f0f0] last:border-b-0 hover:bg-[#f9fafb] cursor-pointer focus:outline-none focus:bg-[#f3f4f6]"
          style={{ gridTemplateColumns: '110px 1fr 110px 110px 110px 1fr 1fr' }}
        >
          <span className="text-[#0e1e20] text-[13px] tabular-nums" style={{ fontFamily: 'Inter, sans-serif' }}>
            {fmtDate(c.created_at)}
          </span>
          <span className="text-[#115e59] text-[13px] underline decoration-[#b7ebdf] underline-offset-2 truncate"
                style={{ fontFamily: 'Inter, sans-serif' }} title={c.name}>
            {c.name || '(unnamed case)'}
          </span>
          <span className="text-[#0e1e20] text-[13px] tabular-nums text-right"
                style={{ fontFamily: 'Inter, sans-serif' }}>
            {Number(c.admin_fee) > 0 ? fmtMoneyFull(c.admin_fee) : '—'}
          </span>
          <span className="text-[#0e1e20] text-[13px] tabular-nums text-right"
                style={{ fontFamily: 'Inter, sans-serif' }}>
            {Number(c.case_manager_fee_amount) > 0 ? fmtMoneyFull(c.case_manager_fee_amount) : '—'}
          </span>
          <span className="text-[#0e1e20] text-[13px] tabular-nums text-right"
                style={{ fontFamily: 'Inter, sans-serif' }}>
            {incomeForFirm(c) > 0 ? fmtMoneyFull(incomeForFirm(c)) : '—'}
          </span>
          <span className="text-[#0e1e20] text-[13px] truncate"
                style={{ fontFamily: 'Inter, sans-serif' }} title={c.plaintiffEmail || ''}>
            {domainOf ? (domainOf(c.plaintiffEmail) || '—') : (c.plaintiffEmail || '—')}
          </span>
          <span className="text-[#0e1e20] text-[13px] truncate"
                style={{ fontFamily: 'Inter, sans-serif' }} title={c.defendantEmail || ''}>
            {domainOf ? (domainOf(c.defendantEmail) || '—') : (c.defendantEmail || '—')}
          </span>
        </div>
      ))}
    </div>
  );
}

/* ── Status dot ────────────────────────────────────────────────────── */
const STATUS_COLOR_MAP = {
  green: '#22c55e',
  yellow: '#f59e0b',
  red: '#ef4444',
  // grey renders as AMBER (#f59e0b) — byte-for-byte identical to the cases
  // table (Cases.jsx STATUS_COLOR_MAP) and case-detail (ProgressTracker
  // COLOR_HEX) so "No Invoice sent" / not-started steps look the same on
  // every surface. statusForCase + CSV read the raw `.color` STRING (never
  // this hex), so the grey sentinel is preserved for them.
  grey: '#f59e0b',
};
function StatusDot({ color, ok }) {
  const resolved = color != null
    ? (STATUS_COLOR_MAP[color] || STATUS_COLOR_MAP.red)
    : (ok ? '#22c55e' : '#ef4444');
  // Canonical: w-2 h-2 (8px) to match Cases / Inquiries pages.
  return <div className="w-2 h-2 rounded-full" style={{ backgroundColor: resolved }} />;
}

/* ── CSV export ────────────────────────────────────────────────────── */
function exportCasesCSV(mediatorName, mediatorCases) {
  const rows = [[
    'Case Name', 'Mediation Date',
    'Plaintiff Onboarding', 'Plaintiff Payment', 'Plaintiff Brief',
    'Defendant Onboarding', 'Defendant Payment', 'Defendant Brief',
  ]];
  mediatorCases.forEach((c) => {
    rows.push([
      c.name, c.date,
      c.plaintiff[0]?.color ?? '', c.plaintiff[1]?.color ?? '', c.plaintiff[2]?.color ?? '',
      c.defendant[0]?.color ?? '', c.defendant[1]?.color ?? '', c.defendant[2]?.color ?? '',
    ]);
  });
  const csv = rows.map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${(mediatorName || 'mediator').replace(/\s+/g, '-')}-cases.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

/* ── Searchable add-as-pill multi-select ───────────────────────────── */
//
// Type to filter a dropdown; click an option to add it as a removable
// chip below the input. Used by both the Law firms and Cases columns
// of the Advanced Filters section — replaces the noisy checkbox list
// pattern with a focused "search → add → see your pills" flow that
// scales to hundreds of cases without overwhelming the UI.
function SearchablePillSelect({ placeholder, options, selectedKeys, getKey, getLabel, getSubLabel, getMeta, onAdd, onRemove, onDelete, deleteConfirmLabel }) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const q = query.trim().toLowerCase();
  const matches = (o) => {
    if (!q) return true;
    const lbl = (getLabel(o) || '').toLowerCase();
    const sub = getSubLabel ? (getSubLabel(o) || '').toLowerCase() : '';
    return lbl.includes(q) || sub.includes(q);
  };
  const available = options.filter((o) => !selectedKeys.has(getKey(o))).filter(matches).slice(0, 50);
  const selectedItems = options.filter((o) => selectedKeys.has(getKey(o)));

  return (
    <div className="flex flex-col gap-2 min-w-0" ref={wrapRef}>
      {/* Search input */}
      <div className="relative">
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none"
             className="absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none">
          <circle cx="6.417" cy="6.417" r="4.667" stroke="#9ca3af" strokeWidth="1.167" />
          <path d="M9.742 9.742l2.508 2.508" stroke="#9ca3af" strokeWidth="1.167" strokeLinecap="round" />
        </svg>
        <input
          type="text"
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder={placeholder}
          className="w-full pl-8 pr-2.5 rounded-lg border border-[#e5e5e5] outline-none focus:border-[#115e59] focus:ring-1 focus:ring-[#115e59]/20"
          style={{ height: 36, fontSize: 13, color: '#0e1e20', fontFamily: 'Inter' }}
        />
        {open && (
          // High z-index so the dropdown floats above the KPI tiles and
          // charts that follow this section. The parent section sets
          // z-index 20 to establish a stacking context; this layer
          // (z-50) lives above any non-positioned siblings below.
          <div className="absolute top-full left-0 right-0 mt-1 overflow-auto rounded-lg border border-[#e5e5e5] bg-white"
               style={{ maxHeight: 240, boxShadow: '0 8px 24px rgba(16,24,40,0.12)', zIndex: 50 }}>
            {available.length === 0 && (
              <div className="px-3 py-2.5 text-[12px] text-[#9ca3af]" style={{ fontFamily: 'Inter' }}>
                {q ? 'No matches.' : 'Type to search…'}
              </div>
            )}
            {available.map((o) => (
              <div
                key={getKey(o)}
                role="button"
                tabIndex={0}
                onClick={() => { onAdd(getKey(o)); setQuery(''); }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onAdd(getKey(o)); setQuery(''); }
                }}
                className="w-full text-left px-3 py-2 hover:bg-[#f9fafb] flex items-center justify-between gap-3 cursor-pointer"
                style={{ background: 'transparent', border: 'none' }}
              >
                <div className="flex flex-col min-w-0 flex-1">
                  <span className="text-[12px] truncate" style={{ color: '#0e1e20', fontFamily: 'Inter' }}>
                    {getLabel(o)}
                  </span>
                  {getSubLabel && getSubLabel(o) && (
                    <span className="text-[10px] truncate" style={{ color: '#9ca3af', fontFamily: 'Inter' }}>
                      {getSubLabel(o)}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {getMeta && getMeta(o) && (
                    <span className="text-[11px] tabular-nums" style={{ color: '#9ca3af', fontFamily: 'Inter' }}>
                      {getMeta(o)}
                    </span>
                  )}
                  {onDelete && (
                    /* Trash icon — excludes (soft-deletes) this option so
                       it disappears from the dropdown across the whole
                       mediator. Stops click propagation so it doesn't
                       also select the row. */
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        const label = getLabel(o);
                        const msg = deleteConfirmLabel
                          ? `${deleteConfirmLabel} "${label}"?`
                          : `Exclude "${label}"? This removes it from the mediator.`;
                        // eslint-disable-next-line no-restricted-globals, no-alert
                        if (window.confirm(msg)) onDelete(getKey(o), o);
                      }}
                      aria-label={`Exclude ${getLabel(o)}`}
                      className="rounded-md hover:bg-[#fee2e2]"
                      style={{ width: 24, height: 24, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', border: 'none', background: 'transparent', cursor: 'pointer', color: '#9ca3af' }}
                      onMouseEnter={(e) => { e.currentTarget.style.color = '#dc2626'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.color = '#9ca3af'; }}
                    >
                      <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
                        <path d="M2.5 3.5h9M5.5 3.5V2.25a.75.75 0 0 1 .75-.75h1.5a.75.75 0 0 1 .75.75V3.5M3.5 3.5l.5 8a1.5 1.5 0 0 0 1.5 1.4h3a1.5 1.5 0 0 0 1.5-1.4l.5-8" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Selected pills — wrap to multiple rows; × removes. The pill
          row is only rendered when there's at least one selection so
          the empty state is just blank space, not noise. */}
      {selectedItems.length > 0 && (
      <div className="flex flex-wrap gap-1.5">
        {selectedItems.map((o) => (
          <span key={getKey(o)}
                className="inline-flex items-center gap-1 rounded-full"
                style={{ background: '#f0fdfa', border: '1px solid #b7ebdf', padding: '3px 4px 3px 10px' }}>
            <span className="text-[12px] truncate max-w-[180px]"
                  style={{ color: '#115e59', fontFamily: 'Inter' }}
                  title={getLabel(o)}>
              {getLabel(o)}
            </span>
            <button
              type="button"
              onClick={() => onRemove(getKey(o))}
              aria-label={`Remove ${getLabel(o)}`}
              className="rounded-full hover:bg-[#b7ebdf]"
              style={{ width: 18, height: 18, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', border: 'none', background: 'transparent', cursor: 'pointer', color: '#115e59' }}
            >
              <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                <path d="M2.5 2.5l5 5M7.5 2.5l-5 5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
              </svg>
            </button>
          </span>
        ))}
      </div>
      )}
    </div>
  );
}

/* ── Advanced Filters — inline expandable section ──────────────────── */
//
// Sits at the top of the page (above the KPIs). Header toggles a panel
// with three columns: Side (narrow, compact segmented control), Law
// firms (searchable add-as-pill), Cases (searchable add-as-pill). Every
// change propagates immediately via onChange — `isPending` drives a
// small pulsing dot so the user sees that recomputation is in flight.
function AdvancedFiltersSection({ coupons, firms, value, onChange, isPending, onCouponExclude }) {
  const [expanded, setExpanded] = useState(false);

  const couponSet = value.couponIds;
  const firmSet = value.firmDomains;
  const totalActive = couponSet.size + firmSet.size + (value.side !== 'all' ? 1 : 0);

  const toggle = (key, val) => {
    const next = new Set(value[key]);
    if (next.has(val)) next.delete(val); else next.add(val);
    onChange({ ...value, [key]: next });
  };
  const reset = () => onChange({ couponIds: new Set(), firmDomains: new Set(), side: 'all' });

  return (
    // NOT overflow-hidden — the searchable dropdowns inside need to
    // float OVER the KPI tiles below. overflow-hidden would clip the
    // dropdown back inside the section, hiding it behind the tiles.
    <div className={`bg-white rounded-[10px] border ${totalActive > 0 ? 'border-[#115e59]' : 'border-[#e5e5e5]'} relative`}
         style={{ fontFamily: 'Inter', zIndex: 20 }}>
      {/* Header — clickable to toggle */}
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-[#f9fafb] transition-colors"
        style={{ background: 'transparent', border: 'none', cursor: 'pointer' }}
      >
        <div className="flex items-center gap-2">
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M1.5 2.5h11l-4 5v4l-3-1.5v-2.5l-4-5z"
                  stroke={totalActive > 0 ? '#115e59' : '#5b5a64'}
                  strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          <span className="text-[13px] font-semibold" style={{ color: '#0e1e20', fontFamily: "'Inter Tight', sans-serif" }}>
            Advanced filters
          </span>
          {totalActive > 0 && (
            <span className="rounded-full text-[10px] font-bold flex items-center justify-center"
                  style={{ background: '#115e59', color: '#fff', minWidth: 18, height: 18, padding: '0 5px' }}>
              {totalActive}
            </span>
          )}
          {isPending && (
            <span className="inline-flex items-center gap-1.5 ml-2"
                  title="Recomputing…">
              <span className="rounded-full animate-pulse" style={{ width: 6, height: 6, background: '#115e59' }} />
              <span className="text-[11px]" style={{ color: '#115e59' }}>Updating…</span>
            </span>
          )}
        </div>
        <div className="flex items-center gap-3">
          {totalActive > 0 && (
            <span
              role="button"
              tabIndex={0}
              onClick={(e) => { e.stopPropagation(); reset(); }}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.stopPropagation(); e.preventDefault(); reset(); } }}
              className="text-[12px] font-medium hover:underline"
              style={{ color: '#115e59', cursor: 'pointer' }}
            >
              Reset all
            </span>
          )}
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none"
               style={{ transform: expanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }}>
            <path d="M3.5 5.25l3.5 3.5 3.5-3.5" stroke="#5b5a64" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
      </button>

      {/* Expanded panel — three columns. Side stacks vertically as
          full-word options; Law firms + Cases use searchable
          add-as-pill selectors. */}
      {expanded && (
        <div className="border-t border-[#e5e5e5] px-4 py-4 grid gap-6 items-start"
             style={{ gridTemplateColumns: '180px 1fr 1.3fr' }}>
          {/* Side — vertical stack, full-word labels (Plaintiff,
              Defendant) so nothing is abbreviated. */}
          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-semibold text-[#5b5a64] uppercase tracking-wide">Side</span>
              {value.side !== 'all' && (
                <button type="button" onClick={() => onChange({ ...value, side: 'all' })}
                        className="text-[11px] font-medium text-[#115e59]"
                        style={{ background: 'transparent', border: 'none', cursor: 'pointer' }}>
                  Clear
                </button>
              )}
            </div>
            <div className="flex flex-col gap-1 p-1 rounded-lg" style={{ background: '#f3f4f6' }}>
              {[
                { key: 'all', label: 'All' },
                { key: 'plaintiff', label: 'Plaintiff' },
                { key: 'defendant', label: 'Defendant' },
              ].map((s) => (
                <button
                  key={s.key}
                  type="button"
                  onClick={() => onChange({ ...value, side: s.key })}
                  className="rounded-md text-[12px] font-semibold text-left transition-colors"
                  style={{
                    padding: '7px 12px',
                    background: value.side === s.key ? '#fff' : 'transparent',
                    color: value.side === s.key ? '#115e59' : '#5b5a64',
                    boxShadow: value.side === s.key ? '0 1px 2px rgba(16,24,40,0.08)' : 'none',
                    border: 'none', cursor: 'pointer',
                  }}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          {/* Law firms — searchable add-as-pill */}
          <div className="flex flex-col gap-2 min-w-0">
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-semibold text-[#5b5a64] uppercase tracking-wide">
                Law firms {firmSet.size > 0 ? `· ${firmSet.size} selected` : ''}
              </span>
              {firmSet.size > 0 && (
                <button type="button" onClick={() => onChange({ ...value, firmDomains: new Set() })}
                        className="text-[11px] font-medium text-[#115e59]"
                        style={{ background: 'transparent', border: 'none', cursor: 'pointer' }}>
                  Clear
                </button>
              )}
            </div>
            <SearchablePillSelect
              placeholder="Search firms by domain…"
              options={firms || []}
              selectedKeys={firmSet}
              getKey={(f) => f.firm}
              getLabel={(f) => f.firm}
              getMeta={(f) => `${f.total} ${f.total === 1 ? 'case' : 'cases'}`}
              onAdd={(k) => toggle('firmDomains', k)}
              onRemove={(k) => toggle('firmDomains', k)}
            />
          </div>

          {/* Coupons — searchable add-as-pill. Replaces the prior Cases
              picker so users can narrow the page to cases that used a
              specific coupon (cases.coupon_id match). */}
          <div className="flex flex-col gap-2 min-w-0">
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-semibold text-[#5b5a64] uppercase tracking-wide">
                Coupons {couponSet.size > 0 ? `· ${couponSet.size} selected` : ''}
              </span>
              {couponSet.size > 0 && (
                <button type="button" onClick={() => onChange({ ...value, couponIds: new Set() })}
                        className="text-[11px] font-medium text-[#115e59]"
                        style={{ background: 'transparent', border: 'none', cursor: 'pointer' }}>
                  Clear
                </button>
              )}
            </div>
            <SearchablePillSelect
              placeholder="Search coupons by code…"
              options={coupons || []}
              selectedKeys={couponSet}
              getKey={(c) => c.id}
              getLabel={(c) => c.coupon_code || `Coupon #${c.id}`}
              getSubLabel={(c) => c.coupon_type || ''}
              onAdd={(k) => toggle('couponIds', k)}
              onRemove={(k) => toggle('couponIds', k)}
              onDelete={onCouponExclude}
              deleteConfirmLabel="Exclude coupon"
            />
          </div>
        </div>
      )}
    </div>
  );
}

/* ── KPI Card — copied 1:1 from Dashboard's KPICardDesign ────────── */
function KpiCard({ title, value, change, periodLabel, invertColors, compareLabel }) {
  return (
    <div className="flex flex-col bg-white rounded-[10px] border border-[#e5e5e5] overflow-hidden flex-1">
      <div className="flex items-center gap-2 px-3.5 py-3 border-b border-[#e5e5e5]">
        <span className="text-xs font-medium text-[#0e1e20]" style={{ fontFamily: 'Inter Tight' }}>
          {title}
        </span>
        {periodLabel ? (
          <span
            className="text-[10px] font-semibold text-gray-600 bg-gray-100 rounded-md whitespace-nowrap"
            style={{ padding: '3px 7px', fontFamily: 'Inter Tight' }}
          >
            {periodLabel}
          </span>
        ) : null}
      </div>
      <div className="flex flex-col items-end justify-end gap-3.5 px-3.5 pt-5 pb-3.5">
        <span className="text-[30px] font-medium text-[#0e1e20] leading-none self-start" style={{ fontFamily: 'Inter Tight' }}>
          {value}
        </span>
        {change === null ? (
          // Caller explicitly suppressed the change badge — no meaningful
          // prior period to compare against (e.g. "All time" with no
          // filters). Reserve a small footprint so KPI cards stay the
          // same height across the row.
          <span style={{ height: 22 }} />
        ) : (
          <ChangeBadge change={change} invertColors={invertColors} compareLabel={compareLabel} />
        )}
      </div>
    </div>
  );
}

/* ── Main page ─────────────────────────────────────────────────────── */
export default function MediatorDetail() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useAuth();
  const [mediator, setMediator] = useState(null);
  const [mediatorCases, setMediatorCases] = useState([]);
  const [loading, setLoading] = useState(true);
  // Surface fetch failures (404 on bad id, 500 from BE, network timeout)
  // so the page renders a clean message instead of staying blank with the
  // error only living in the console.
  const [mediatorError, setMediatorError] = useState(null);
  // Sequence guard: clicking between mediators in quick succession could
  // otherwise let an older response overwrite the newer one. Each
  // fetchMediator run bumps the counter; response handlers bail when a
  // newer run has superseded them.
  const fetchSeqRef = useRef(0);
  // Active detail tab. 'overview' keeps the existing analytics dashboard
  // exactly as-is; the other three are the ported Scale-portal mediator
  // tools (booking link, documents, editable profile).
  const [activeTab, setActiveTab] = useState('overview');
  // Page-level toast shared by the new tabs (the analytics dashboard's own
  // FeeEditor keeps its independent Toast). Rendered once near the page root.
  const [pageToast, setPageToast] = useState(null);
  const notify = useCallback((t) => setPageToast(t), []);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const [openFirm, setOpenFirm] = useState(null);
  // Attorney drill-down — opened from FirmDetailView's Attorney column.
  // Stores the attorney's email; clearing it (or backing out of firm
  // detail) returns to the firm cases list.
  const [openAttorney, setOpenAttorney] = useState(null);
  // Lifted into the parent so the user's weekly/monthly choice survives
  // remounting when they enter/leave the firm-detail view.
  const [bookingGranularity, setBookingGranularity] = useState('monthly');
  const [momGranularity, setMomGranularity] = useState('monthly');
  // Inquiries Over Time has its own range (1M/3M/6M/YTD) so it can be
  // narrowed independently of the page-level Date pill. Lifted to the
  // parent because the chart is rendered via an inline IIFE that can't
  // host hooks itself.
  // Fixed at 'all' now that the per-chart range dropdown is removed (weekly/
  // monthly toggle is the only graph-level control).
  const [inquiriesChartRange] = useState('all');
  // Weekly/monthly toggle for Inquiries Over Time (matches MoM/Booking). Weekly
  // buckets raw inquiry rows by week; monthly uses the BE's inquiriesMonthly.
  const [inquiriesGranularity, setInquiriesGranularity] = useState('monthly');
  // FilterBar state — mirrors Dashboard's filter wiring so the pills,
  // dropdowns and date picker look and behave identically.
  const [dateFilter, setDateFilter] = useState('all');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [mediatorsList, setMediatorsList] = useState([]);
  // Advanced filters — cases / firms / side. Empty Sets and side='all'
  // means "no constraint" (everything passes). State updates are wrapped
  // in a React transition so the filter UI stays interactive while the
  // page recomputes KPIs / charts / tables — `isFiltering` powers the
  // "Updating…" pulse next to the section header.
  const [advancedFilters, setAdvancedFiltersInternal] = useState({
    couponIds: new Set(),
    firmDomains: new Set(),
    side: 'all',
  });
  const [isFiltering, startFiltering] = useTransition();
  const setAdvancedFilters = useCallback((next) => {
    startFiltering(() => {
      setAdvancedFiltersInternal(next);
    });
  }, []);

  // Fetch the full mediators list once for the FilterBar's MediatorFilter
  // — mirrors the Mediators table 1:1: every row in scope is included,
  // same display-name fallback chain (name → firstName+lastName →
  // 'Unknown'). The earlier "filter unnamed mediators" attempt hid
  // valid rows from the switcher.
  useEffect(() => {
    api.get('/mediators?limit=200')
      .then((res) => {
        const arr = res.data?.data || [];
        const seen = new Set();
        const list = arr
          .map((m) => {
            const fullName =
              m.name
              || `${m.firstName || ''} ${m.lastName || ''}`.trim()
              || 'Unknown';
            return {
              userId: m.userId,
              name: fullName,
              id: m.mediatorId || m.id,
            };
          })
          .filter((m) => {
            // Dedup so two records with the same userId (or same name +
            // null userId) don't both appear in the dropdown.
            const key = m.userId || `name:${m.name.trim().toLowerCase()}`;
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
          })
          .sort((a, b) => a.name.localeCompare(b.name));
        setMediatorsList(list);
      })
      .catch(() => setMediatorsList([]));
  }, []);

  // Cases narrowed to the active date filter. dateRangeForFilter is the
  // same helper Dashboard uses, so presets / custom ranges behave
  // identically across pages.
  const dateRange = useMemo(
    () => dateRangeForFilter(dateFilter, customStart, customEnd),
    [dateFilter, customStart, customEnd],
  );
  const dateFilteredCases = useMemo(() => {
    const { startDate, endDate } = dateRange;
    if (!startDate && !endDate) return mediatorCases;
    const startMs = startDate ? Date.parse(`${startDate}T00:00:00`) : -Infinity;
    const endMs = endDate ? Date.parse(`${endDate}T23:59:59`) : Infinity;
    return mediatorCases.filter((c) => {
      const raw = c.mediation_date || c.created_at;
      const t = raw ? Date.parse(raw.length === 10 ? `${raw}T00:00:00` : raw) : NaN;
      if (!Number.isFinite(t)) return true;
      return t >= startMs && t <= endMs;
    });
  }, [mediatorCases, dateRange]);

  // Label used on the KPI cards' period chip + on each ChangeBadge's
  // "vs <X>" suffix so the pills track the active filter.
  const periodLabel = useMemo(() => {
    if (dateFilter === 'custom' && (customStart || customEnd)) {
      return `${customStart || '…'} – ${customEnd || '…'}`;
    }
    const preset = DEFAULT_DATE_PRESETS.find((p) => p.value === dateFilter);
    return preset?.label || 'All time';
  }, [dateFilter, customStart, customEnd]);

  // ── Advanced filter application ──────────────────────────────────────
  // Domain extraction mirrors the BE's domainOf via the SHARED constant
  // in src/utils/personalEmailDomains.js — no more drift between the
  // FE matcher and the BE aggregation.
  const domainOf = useCallback((email) => {
    if (!email || typeof email !== 'string') return null;
    const at = email.lastIndexOf('@');
    if (at < 0) return null;
    const d = email.slice(at + 1).trim().toLowerCase();
    if (!d || !d.includes('.')) return null;
    if (PERSONAL_EMAIL_DOMAINS.has(d)) return PERSONAL_EMAIL_BUCKET_LABEL;
    return d;
  }, []);

  const hasAdvancedFilters =
    advancedFilters.couponIds.size > 0 ||
    advancedFilters.firmDomains.size > 0 ||
    advancedFilters.side !== 'all';

  const appliedCases = useMemo(() => {
    if (!hasAdvancedFilters) return dateFilteredCases;
    const { couponIds, firmDomains, side } = advancedFilters;
    return dateFilteredCases.filter((c) => {
      // Coupon filter: narrow to cases whose coupon_id matches a
      // selected coupon. Cases with no coupon never pass the filter
      // when any coupon is selected.
      if (couponIds.size > 0 && !couponIds.has(c.coupon_id)) return false;
      const pDom = domainOf(c.plaintiffEmail);
      const dDom = domainOf(c.defendantEmail);
      const pFirmHit = pDom && firmDomains.has(pDom);
      const dFirmHit = dDom && firmDomains.has(dDom);
      if (firmDomains.size > 0) {
        if (side === 'plaintiff') return pFirmHit;
        if (side === 'defendant') return dFirmHit;
        return pFirmHit || dFirmHit;
      }
      // No firm filter: side restricts to cases where THIS mediator
      // worked with the named side present on the case.
      if (side === 'plaintiff') return !!c.plaintiffEmail;
      if (side === 'defendant') return !!c.defendantEmail;
      return true;
    });
  }, [dateFilteredCases, advancedFilters, hasAdvancedFilters, domainOf]);

  // Recompute the Law Firms breakdown when advanced filters narrow the
  // Firms aggregated from cases that pass the DATE filter only (ignoring
  // advanced filters). Drives the Advanced Filters' firm picker so the
  // dropdown lists only firms with cases in the active window — picking
  // a firm with zero cases in the window would yield silently-empty
  // results which is a confusing UX dead end.
  const dateFilteredLawFirms = useMemo(() => {
    const { startDate, endDate } = dateRange;
    if (!startDate && !endDate) return mediator?.lawFirms || [];
    const firmsMap = new Map();
    for (const c of dateFilteredCases) {
      // Income source MUST match BE's lawFirms aggregation (paid `amount`,
      // not paid `grand_total`) so the Income column stays consistent
      // between filtered and unfiltered views and never disagrees with
      // the Mediator Revenue KPI definition.
      const rev = Number(c.revenueInvoicedGross) || 0;
      const pDom = domainOf(c.plaintiffEmail);
      const dDom = domainOf(c.defendantEmail);
      const touched = new Set();
      const bump = (dom, role) => {
        if (!dom) return;
        let agg = firmsMap.get(dom);
        if (!agg) {
          agg = { firm: dom, total: 0, plaintiff: 0, defendant: 0, income: 0, lastCaseName: null, lastCaseDate: null };
          firmsMap.set(dom, agg);
        }
        if (role === 'plaintiff') agg.plaintiff += 1;
        if (role === 'defendant') agg.defendant += 1;
        if (!touched.has(dom)) {
          agg.total += 1;
          agg.income += rev;
          touched.add(dom);
        }
      };
      bump(pDom, 'plaintiff');
      bump(dDom, 'defendant');
    }
    return Array.from(firmsMap.values()).sort((a, b) => b.total - a.total);
  }, [dateFilteredCases, dateRange, mediator?.lawFirms, domainOf]);

  // case set. Mirrors the BE's aggregation logic in mediatorController.js
  // — plus the Side filter (plaintiff/defendant) gates which role gets
  // counted. Firms that never appear on the active side fall out of the
  // table entirely.
  const appliedLawFirms = useMemo(() => {
    // Only fall back to the BE's all-time list when nothing is filtered. When a
    // date range OR advanced filter is active, recompute from the filtered
    // cases so the Law Firms breakdown respects the filters.
    const hasDateFilter = !!(dateRange?.startDate || dateRange?.endDate);
    if (!hasAdvancedFilters && !hasDateFilter) return mediator?.lawFirms || [];
    const { side } = advancedFilters;
    const firmsMap = new Map();
    for (const c of appliedCases) {
      const pDom = domainOf(c.plaintiffEmail);
      const dDom = domainOf(c.defendantEmail);
      const touched = new Set();
      // Counts only — income is credited PER DOMAIN below from
      // revenueInvoicedGrossByDomain so each firm gets its OWN invoiced
      // share, not the full case total.
      const bump = (dom, role) => {
        if (!dom) return;
        // Side filter — only let the active role contribute. With
        // side='plaintiff' we skip every defendant bump (and vice versa).
        if (side === 'plaintiff' && role !== 'plaintiff') return;
        if (side === 'defendant' && role !== 'defendant') return;
        let agg = firmsMap.get(dom);
        if (!agg) {
          agg = { firm: dom, total: 0, plaintiff: 0, defendant: 0, income: 0, lastCaseName: null, lastCaseDate: null };
          firmsMap.set(dom, agg);
        }
        if (role === 'plaintiff') agg.plaintiff += 1;
        if (role === 'defendant') agg.defendant += 1;
        if (!touched.has(dom)) {
          agg.total += 1;
          touched.add(dom);
          const when = c.mediation_date || c.created_at || null;
          if (when && (!agg.lastCaseDate || when > agg.lastCaseDate)) {
            agg.lastCaseDate = when;
            agg.lastCaseName = c.name || null;
          }
        }
      };
      bump(pDom, 'plaintiff');
      if (dDom && dDom !== pDom) bump(dDom, 'defendant');

      // Per-domain income: credit each firm only its OWN invoiced gross on
      // this case. Only add to firms that were counted above (respecting the
      // side filter) so income never resurrects a firm that fell out.
      const byDomain = c.revenueInvoicedGrossByDomain || {};
      for (const [dom, amount] of Object.entries(byDomain)) {
        const agg = firmsMap.get(dom);
        if (agg) agg.income += Number(amount) || 0;
      }
    }
    return Array.from(firmsMap.values())
      .filter((f) => f.total > 0)
      .sort((a, b) => (b.total - a.total) || (b.income - a.income))
      .map((f, idx) => ({ rank: idx + 1, ...f }));
  }, [appliedCases, hasAdvancedFilters, advancedFilters, dateRange, mediator?.lawFirms, domainOf]);

  // KPI values — when ANY filter (date or advanced) is active, KPIs are
  // computed from the filtered subset; otherwise they use the BE
  // headline numbers so cardinal "All time" stays exact.
  const kpiFiltered = hasAdvancedFilters || dateFilter !== 'all';
  // Dashboard parity:
  //   Cases       = cases where mediation_date falls in the selected range
  //   Cases Booked = cases where created_at falls in the selected range
  //                  AND mediation_date IS NOT NULL
  // Both computed from the full case list (mediatorCases), not the
  // created_at-filtered appliedCases, so each tile uses the correct
  // date column independently — matching the dashboard's separate
  // scopeCases (mediation_date) vs casesBookedQuery (created_at).
  const { kpiCases, kpiCasesBooked } = useMemo(() => {
    if (!kpiFiltered) {
      return {
        kpiCases: mediator?.analytics?.totalCases ?? 0,
        kpiCasesBooked: mediator?.analytics?.casesBookedFromDash ?? mediator?.casesBooked?.total ?? 0,
      };
    }
    const { startDate, endDate } = dateRange;
    const lo = startDate || '';
    const hi = endDate || '9999-12-31';
    let cases = 0;
    let booked = 0;
    for (const c of appliedCases) {
      const md = (c.mediation_date || c.date || '').slice(0, 10);
      const ca = (c.created_at || '').slice(0, 10);
      if (md && md >= lo && md <= hi) cases++;
      if (md && ca && ca >= lo && ca <= hi) booked++;
    }
    return { kpiCases: cases, kpiCasesBooked: booked };
  }, [kpiFiltered, appliedCases, dateRange, mediator]);
  // Mediator Revenue = paid `amount` only (excludes admin fee, Stripe
  // processing fee, and any unpaid invoices). Matches the Scale portal's
  // Mediator Revenue KPI exactly. BE returns `analytics.totalRevenueByAmount`
  // for the all-time number and per-case `revenueByAmount` for filtered
  // views. Falls back to older paid totals for back-compat.
  //
  // Distinguish "data unavailable" (BE response missing the field — the
  // analytics block didn't load) from "genuinely zero" so the KPI tile
  // can render "—" instead of $0 when there's no data to summarize.
  // Filtered mode always has a defined sum (could be 0), so it's safe to
  // treat as a number; all-time mode reads from the analytics block.
  const kpiRevenueRaw = kpiFiltered
    ? appliedCases.reduce((s, c) => s + (Number(c.revenueInvoiced) || 0), 0)
    : (mediator?.analytics?.totalRevenueByAmount
        ?? mediator?.analytics?.totalRevenue
        ?? null);
  const kpiRevenueAvailable = kpiRevenueRaw !== null && kpiRevenueRaw !== undefined;
  const kpiRevenue = kpiRevenueAvailable ? kpiRevenueRaw : 0;
  // Average booking time = avg days from booked (created_at) to mediation
  // date. Smaller is better → ChangeBadge invertColors.
  const avgBookingTime = useMemo(() => {
    const src = kpiFiltered ? appliedCases : (mediator?.cases || []);
    let sum = 0;
    let n = 0;
    for (const c of src) {
      if (!c.created_at || !c.mediation_date) continue;
      const ct = Date.parse(c.created_at);
      const mt = Date.parse(c.mediation_date.length === 10 ? `${c.mediation_date}T00:00:00Z` : c.mediation_date);
      if (!Number.isFinite(ct) || !Number.isFinite(mt)) continue;
      const days = Math.max(0, Math.round((mt - ct) / 86400000));
      sum += days;
      n += 1;
    }
    // Return null (no bookings to compute from) so the consumer can
    // distinguish "data missing" from "genuinely zero days".
    return n > 0 ? { avg: Math.round(sum / n), n } : { avg: null, n: 0 };
  }, [appliedCases, mediator?.cases, kpiFiltered]);
  // "0 days" is a real value (same-day bookings) — only show "—" when
  // we have nothing to compute an average from.
  const avgBookingTimeLabel = avgBookingTime.avg === null
    ? '—'
    : `${avgBookingTime.avg} day${avgBookingTime.avg === 1 ? '' : 's'}`;

  // ── Dashboard-parity change pills ────────────────────────────────────
  // 1:1 port of server/utils/dashboardMath.js → computeChange so the FE
  // pill math is identical to Dashboard's BE result:
  //   prev > 0       → ((cur-prev)/prev)*100, rounded — UNCAPPED.
  //   prev = 0, cur > 0 → isNew:true, value=cur (renders "+N new")
  //   prev = 0, cur < 0 → -100%
  //   prev = 0, cur = 0 → 0%
  const computeChangeValue = useCallback((current, previous) => {
    const cur = Number(current) || 0;
    const prev = Number(previous) || 0;
    const diff = cur - prev;
    // When prev=0 and cur>0, show "+N new" instead of "+100%".
    if (prev === 0 && cur > 0) {
      return {
        value: cur,
        percent: null,
        direction: 'up',
        isNew: true,
      };
    }
    let percent;
    if (prev > 0) {
      percent = Math.round((diff / prev) * 100);
    } else if (cur < 0) {
      percent = -100;
    } else {
      percent = 0;
    }
    return {
      value: diff,
      percent,
      direction: diff > 0 ? 'up' : diff < 0 ? 'down' : 'flat',
      isNew: false,
    };
  }, []);

  // Apply the same advanced-filter rules to an arbitrary case — reused
  // for the previous-period calculation so the comparison is apples-to-
  // apples with the current period.
  const applyAdvanced = useCallback((c) => {
    if (!hasAdvancedFilters) return true;
    const { couponIds, firmDomains, side } = advancedFilters;
    if (couponIds.size > 0 && !couponIds.has(c.coupon_id)) return false;
    const pDom = domainOf(c.plaintiffEmail);
    const dDom = domainOf(c.defendantEmail);
    const pFirmHit = pDom && firmDomains.has(pDom);
    const dFirmHit = dDom && firmDomains.has(dDom);
    if (firmDomains.size > 0) {
      if (side === 'plaintiff') return pFirmHit;
      if (side === 'defendant') return dFirmHit;
      return pFirmHit || dFirmHit;
    }
    if (side === 'plaintiff') return !!c.plaintiffEmail;
    if (side === 'defendant') return !!c.defendantEmail;
    return true;
  }, [advancedFilters, hasAdvancedFilters, domainOf]);

  // KPI change windows.
  //   With a date filter active → current = the chosen range, previous
  //     = a same-length range immediately before it.
  //   Without a date filter   → current = trailing 30 days, previous =
  //     the 30 days before that. A strict "this calendar month"
  //     comparison collapses to 0% for any mediator whose cases land in
  //     other months, which is exactly the broken pill the user saw.
  const currentWindow = useMemo(() => {
    const { startDate, endDate } = dateRange;
    if (startDate && endDate) {
      // Parse as UTC to stay consistent with dateFilteredCases (which now
      // also parses range bounds as UTC). Mixing local + UTC interpretation
      // here let cases pass dateFilteredCases but be excluded from
      // currentWindow, so the KPI value and the change pill could be
      // computed against subtly different sets at midnight-UTC boundaries.
      return {
        startMs: Date.parse(`${startDate}T00:00:00Z`),
        endMs: Date.parse(`${endDate}T23:59:59Z`),
      };
    }
    const now = Date.now();
    return { startMs: now - 30 * 86400000, endMs: now };
  }, [dateRange]);

  const prevDateRange = useMemo(() => {
    const { startDate, endDate } = dateRange;
    if (startDate && endDate) {
      // UTC for parity with currentWindow + dateFilteredCases.
      const startMs = Date.parse(`${startDate}T00:00:00Z`);
      const endMs = Date.parse(`${endDate}T23:59:59Z`);
      const length = endMs - startMs;
      return { startMs: startMs - length - 1, endMs: startMs - 1 };
    }
    const now = Date.now();
    return { startMs: now - 60 * 86400000, endMs: now - 30 * 86400000 - 1 };
  }, [dateRange]);

  // Compute current-vs-previous values for each KPI in one pass over
  // `mediatorCases`. Cheaper than four separate filters and keeps the
  // advanced-filter logic centralised.
  const filterChanges = useMemo(() => {
    let curCases = 0, prevCases = 0;
    let curBooked = 0, prevBooked = 0;
    let curRevenue = 0, prevRevenue = 0;
    let curTtsSum = 0, curTtsN = 0;
    let prevTtsSum = 0, prevTtsN = 0;
    for (const c of mediatorCases) {
      if (!applyAdvanced(c)) continue;
      const cAt = c.created_at ? Date.parse(c.created_at) : NaN;
      if (!Number.isFinite(cAt)) continue;
      const isCurrent = cAt >= currentWindow.startMs && cAt <= currentWindow.endMs;
      const isPrev = cAt >= prevDateRange.startMs && cAt <= prevDateRange.endMs;
      if (!isCurrent && !isPrev) continue;
      // Revenue = paid `amount` only (matches Scale's totalRevenueByAmount
      // definition + the kpiRevenue calc above so the change pill compares
      // paid-only vs paid-only).
      const billed = Number(c.revenueInvoiced) || 0;
      const hasBooked = !!c.mediation_date;
      let ttsDays = null;
      if (c.mediation_date) {
        const mt = Date.parse(c.mediation_date.length === 10 ? `${c.mediation_date}T00:00:00Z` : c.mediation_date);
        if (Number.isFinite(mt)) ttsDays = Math.max(0, Math.round((mt - cAt) / 86400000));
      }
      if (isCurrent) {
        curCases += 1;
        if (hasBooked) curBooked += 1;
        curRevenue += billed;
        if (ttsDays != null) { curTtsSum += ttsDays; curTtsN += 1; }
      } else {
        prevCases += 1;
        if (hasBooked) prevBooked += 1;
        prevRevenue += billed;
        if (ttsDays != null) { prevTtsSum += ttsDays; prevTtsN += 1; }
      }
    }
    return {
      cases: computeChangeValue(curCases, prevCases),
      casesBooked: computeChangeValue(curBooked, prevBooked),
      revenue: computeChangeValue(Math.round(curRevenue), Math.round(prevRevenue)),
      // Avg Booking Time has no meaningful change when neither window
      // contains any booking. Returning null suppresses the pill — better
      // than showing "0% vs previous period" when there's nothing to
      // compare on either side.
      avgBookingTime: (curTtsN === 0 && prevTtsN === 0)
        ? null
        : computeChangeValue(
            curTtsN > 0 ? Math.round(curTtsSum / curTtsN) : 0,
            prevTtsN > 0 ? Math.round(prevTtsSum / prevTtsN) : 0,
          ),
    };
  }, [mediatorCases, applyAdvanced, currentWindow, prevDateRange, computeChangeValue]);

  // Comparison label — must describe the ACTUAL window the pill is
  // comparing against. For dateFilter='all' the window is "trailing 30
  // days vs the 30 days before that" (not calendar months), so the
  // label reads "previous 30 days". Custom range → "previous period".
  // Other presets map to their natural prior-period label.
  const compareLabel = useMemo(() => {
    if (dateFilter === 'custom') return 'previous period';
    const map = {
      all: 'previous 30 days',
      upcoming: 'previous window',
      thisweek: 'last week',
      nextweek: 'previous week',
      lastmonth: 'previous month',
      last30: 'previous 30 days',
      last90: 'previous 90 days',
    };
    return map[dateFilter] || 'previous 30 days';
  }, [dateFilter]);

  // Always use FE-computed period-vs-period changes — the BE's MoM
  // values are calendar-month-bound and collapse to 0% for any mediator
  // whose recent cases happen to land outside the current calendar month.
  // FE windows are filter-aware (current = active range, previous = same
  // length immediately before) and default to a rolling 30-day window so
  // the pills always convey real momentum.
  //
  // When the user has NEITHER a date filter NOR advanced filters active,
  // the KPI tile shows the BE's all-time headline number. Pairing an
  // all-time number with a rolling-30-day delta makes the percent look
  // like it's OF the all-time total, which it isn't. Suppress the change
  // badges entirely in that mode so the headline number reads cleanly.
  const showChange = kpiFiltered;
  const kpiCasesChange = showChange ? filterChanges.cases : null;
  const kpiRevenueChange = showChange ? filterChanges.revenue : null;
  const kpiCasesBookedChange = showChange ? filterChanges.casesBooked : null;
  const kpiAvgBookingChange = showChange ? filterChanges.avgBookingTime : null;

  // Map a raw case object (from either backend) into the FE table shape.
  // CM-BE source includes per-case `admin_fee` + per-party `*Status` text
  // (used for the Attorney Detail "Total Admin Fee" stat + the table's
  // hover tooltips). Scale-BE omits both — we keep the keys with safe
  // defaults so the JSX renders without crashing and degrades to "—" /
  // empty tooltips. Cm-delta overlay (admin_fee, statusText) is applied
  // by the caller AFTER mapping.
  const mapCaseRow = (c) => ({
    id: String(c.id),
    caseNumber: c.case_number || null,
    name: c.case_name || c.caseName || '',
    date: c.mediation_date
      ? new Date(c.mediation_date + 'T00:00:00').toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
      : '',
    dateRaw: c.mediation_date || '',
    amount: Number(c.amount) || 0,                  // paid grand_total (legacy Income chart)
    revenueByAmount: Number(c.revenueByAmount) || 0,// paid amount (legacy Scale parity)
    revenueInvoiced: Number(c.revenueInvoiced) || 0,// paid + pending NET amount — KPIs, MoM, Cases parity
    revenueInvoicedGross: Number(c.revenueInvoicedGross) || 0,// gross grand_total — Law Firm views only
    revenueInvoicedGrossByDomain: c.revenueInvoicedGrossByDomain || {},// per-firm-domain invoiced gross — firm/attorney drill-downs
    billed: Number(c.billed) || 0,                  // ALL invoiced grand_total — drives KPI + MoM Income
    admin_fee: Number(c.admin_fee) || 0,            // PAID application_fee per case — Attorney Detail (CM-BE only)
    paid_at: c.paid_at || null,
    invoiced_at: c.invoiced_at || null,             // earliest invoice creation date (paid or not) — MoM Income bucket
    created_at: c.created_at || null,
    mediation_date: c.mediation_date || null,
    status: c.status || null,
    coupon_id: c.coupon_id ?? null,
    is_gsc_case: !!c.is_gsc_case,
    is_car_case: !!c.is_car_case,
    plaintiffEmail: c.plaintiff?.email || null,
    defendantEmail: c.defendant?.email || null,
    // `*Status` text is CM-only (it's how CM-BE encodes reminder windows
    // like "Reminder sent at 5 days"). Scale-BE returns the color but no
    // text → tooltip just shows the dot label, which is acceptable.
    plaintiff: [
      { label: 'Onboarding', color: c.plaintiff?.onboarding || 'grey', statusText: c.plaintiff?.onboardingStatus },
      { label: 'Payment',    color: c.plaintiff?.payment    || 'grey', statusText: c.plaintiff?.paymentStatus },
      { label: c.briefText || 'Brief',      color: c.plaintiff?.brief      || 'grey', statusText: c.plaintiff?.briefStatus },
    ],
    defendant: [
      { label: 'Onboarding', color: c.defendant?.onboarding || 'grey', statusText: c.defendant?.onboardingStatus },
      { label: 'Payment',    color: c.defendant?.payment    || 'grey', statusText: c.defendant?.paymentStatus },
      { label: c.briefText || 'Brief',      color: c.defendant?.brief      || 'grey', statusText: c.defendant?.briefStatus },
    ],
  });

  // Per-case Case Manager fee — read from the CASE's own stored fee
  // (case_manager_fee_amount/type). Only cases that have a fee count; a case
  // without one contributes nothing. fixed = amount; percentage = amount% of
  // the case's billed rate. Mirrors cm-be's utils/caseManagerFee.js so the
  // per-case column + "Total CM Fee" stat tie out to the backend tiles.
  const cmFeeForCase = (c) => {
    // Prefer the BE-computed per-case fee (config + invoiced revenue incl.
    // hourly/GSC). It ties out exactly to the Dashboard/Billing CM-fee tiles.
    if (c?.cmFee != null) return Number(c.cmFee) || 0;
    // Legacy fallback (only when the BE didn't supply cmFee): per-case stored
    // fee. NOTE: percentage off rate columns is approximate — rate columns are
    // null on most cases — so this path can under-report; prefer the BE value.
    const amt = Number(c?.case_manager_fee_amount) || 0;
    if (!amt) return 0;
    if ((c?.case_manager_fee_type || 'fixed') === 'percentage') {
      const rate = c?.slot_type === 'fullday' ? (Number(c?.full_day_rate) || 0)
        : c?.slot_type === 'halfday' ? (Number(c?.half_day_rate) || 0)
        : (Number(c?.hourly_rate) || Number(c?.half_day_rate) || 0);
      return (rate * amt) / 100;
    }
    return amt;
  };

  const fetchMediator = useCallback(() => {
    setLoading(true);
    setMediatorError(null);
    const mySeq = ++fetchSeqRef.current;

    // Dual-call migration: Scale-BE is the source of truth for the
    // mediator analytics aggregation (totalCases, totalRevenue*, MoM
    // change, lawFirms, casesBooked, per-case money fields). CM-BE is
    // hit in parallel for the CM-only delta:
    //   - the integer-id → user_id (UUID) resolution required to call
    //     Scale-BE (URL param `id` is the numeric mediator_id)
    //   - per-case `admin_fee` (Scale-BE intentionally omits — see
    //     scalemediation-be/src/controller/mediatorController.js:495-498)
    //   - per-party `*Status` text strings (Scale-BE returns colors only)
    //   - `inquiriesMonthly` series (no `cm_inquiries` table on Scale)
    //
    // CM-BE remains the source for the auth gate (404/403) — if it fails
    // we surface the error verbatim and skip Scale-BE; if Scale-BE then
    // fails we degrade silently to the CM-BE response (current behavior).
    api.get(`/mediators/${id}`)
      .then(async (cmRes) => {
        if (mySeq !== fetchSeqRef.current) return;
        const cm = cmRes.data?.data || cmRes.data || {};
        const userUuid = cm?.userId || cm?.user_id || null;

        // Fire Scale-BE only when we successfully resolved a UUID. If
        // CM-BE returned the mediator but no user_id (impossible in
        // practice but defensive), we just use the CM shape directly.
        let scale = null;
        let billingSummary = null;
        let dashboardStats = null;
        if (userUuid) {
          // Best-effort detail widgets — a failure here (e.g. the analytics proxy
          // returning 401) must NOT log the user out. The core /mediators/:id call
          // above still logs out on a genuine session 401.
          const [scaleResult, billingResult, dashResult] = await Promise.allSettled([
            api.get(`/scale-proxy/mediators/${userUuid}/analytics`, { skipAuthRedirect: true }),
            api.get(`/billing/summary?mediatorId=${userUuid}`, { skipAuthRedirect: true }),
            api.get(`/dashboard?mediatorId=${userUuid}`, { skipAuthRedirect: true }),
          ]);
          if (mySeq !== fetchSeqRef.current) return;
          if (scaleResult.status === 'fulfilled') {
            scale = scaleResult.value?.data?.data || scaleResult.value?.data || null;
          }
          if (billingResult.status === 'fulfilled') {
            billingSummary = billingResult.value?.data?.data || billingResult.value?.data || null;
          }
          if (dashResult.status === 'fulfilled') {
            dashboardStats = dashResult.value?.data?.data || dashResult.value?.data || null;
          }
        }
        if (mySeq !== fetchSeqRef.current) return;

        // Build the per-case admin_fee + statusText map from CM-BE so we
        // can overlay it on the Scale-BE cases list when Scale is the
        // primary source. Keyed by string(c.id) for stable matching.
        const cmCaseDelta = new Map();
        for (const c of cm.cases || []) {
          cmCaseDelta.set(String(c.id), {
            admin_fee: Number(c.admin_fee) || 0,
            // BE-computed per-case CM fee (config + invoiced revenue incl hourly)
            // and net invoiced revenue — authoritative, so the FE never re-derives
            // the % fee off rate columns (which are null on most cases).
            cmFee: c.cmFee,
            revenueInvoiced: c.revenueInvoiced,
            plaintiff_onboardingStatus: c.plaintiff?.onboardingStatus,
            plaintiff_paymentStatus: c.plaintiff?.paymentStatus,
            plaintiff_briefStatus: c.plaintiff?.briefStatus,
            // CM-BE has richer onboarding/brief/payment colors than Scale-BE.
            // Scale-BE's per-case `partyDot` has NO GSC/hourly awareness, so
            // GSC payment comes back grey there. CM-BE's computePartyProgress
            // applies the same GSC/hourly rules as the Cases page, so prefer
            // its colors for ALL THREE lights (incl. payment) on the Scale path.
            plaintiff_onboardingColor: c.plaintiff?.onboarding,
            plaintiff_paymentColor: c.plaintiff?.payment,
            plaintiff_briefColor: c.plaintiff?.brief,
            defendant_onboardingStatus: c.defendant?.onboardingStatus,
            defendant_paymentStatus: c.defendant?.paymentStatus,
            defendant_briefStatus: c.defendant?.briefStatus,
            defendant_onboardingColor: c.defendant?.onboarding,
            defendant_paymentColor: c.defendant?.payment,
            defendant_briefColor: c.defendant?.brief,
          });
        }

        if (scale) {
          // Scale-BE is the source of truth. Build the merged shape the
          // JSX consumes: Scale's analytics + casesBooked + change +
          // lawFirms + coupons, augmented with CM-BE's inquiriesMonthly
          // + license fields (Scale-BE doesn't return licenseNumber).
          const mergedAnalytics = { ...scale.analytics };
          if (billingSummary) {
            // Mediator Revenue KPI = INVOICED revenue = collected (paid) +
            // outstanding (pending), incl. hourly/GSC invoices — so it matches
            // the cases list (per-case revenueInvoiced) and the filtered-mode
            // KPI. The backend `mediatorRevenue` field is PAID-ONLY (= RPC
            // stats.amount_paid), so on its own it zeroes out mediators whose
            // revenue is all pending (e.g. Ben Keener: $0 vs true $20,625) and
            // halves part-pending ones (Drew Lewis: $2,850 vs true $5,700).
            // Prefer the BE invoiced field; fall back to summing collected +
            // outstanding for older BE responses.
            const invoicedRevenue =
              billingSummary.mediatorRevenueInvoiced ??
              ((Number(billingSummary.mediatorRevenue ?? billingSummary.collected) || 0) +
                (Number(billingSummary.mediatorRevenueOutstanding ?? billingSummary.outstanding) || 0));
            mergedAnalytics.totalRevenueByAmount = invoicedRevenue;
            mergedAnalytics.totalRevenue = invoicedRevenue;
            mergedAnalytics.billingOutstanding = billingSummary.mediatorRevenueOutstanding ?? 0;
          }
          if (dashboardStats) {
            mergedAnalytics.totalCases = dashboardStats.totalCases ?? mergedAnalytics.totalCases;
            mergedAnalytics.casesBookedFromDash = dashboardStats.casesBooked ?? null;
          }
          const merged = {
            ...scale,
            analytics: mergedAnalytics,
            inquiriesMonthly: cm?.inquiriesMonthly || scale.inquiriesMonthly || [],
            // Raw inquiry rows (created_at + status) for the weekly view.
            inquiries: cm?.inquiries || scale.inquiries || [],
            licenseNumber: cm?.licenseNumber || cm?.license_number || scale?.licenseNumber || null,
            license_number: cm?.license_number || cm?.licenseNumber || scale?.license_number || null,
            case_manager_fee_type: cm?.case_manager_fee_type || null,
            case_manager_fee_amount: cm?.case_manager_fee_amount ?? null,
          };
          setMediator(merged);

          // Map Scale-BE cases, overlaying CM-BE's admin_fee + statusText
          // by id. Cases on Scale that aren't in CM (shouldn't happen —
          // same DB) just get admin_fee=0 + empty statusText.
          const mappedCases = (scale.cases || []).map((c) => {
            const base = mapCaseRow(c);
            const delta = cmCaseDelta.get(String(c.id));
            // Per-case CM fee: prefer the CM-BE computed value (delta.cmFee);
            // fall back to the local re-derive only if CM-BE didn't supply it.
            base.case_manager_fee_amount =
              delta && delta.cmFee != null ? (Number(delta.cmFee) || 0) : cmFeeForCase(c);
            if (delta) {
              base.admin_fee = delta.admin_fee;
              // Build the dot colors PURELY from CM-BE (delta.*Color), which
              // runs the exact same computePartyProgress as the cases table —
              // do NOT fall back to scale-be's payment/onboarding/brief, which
              // are GSC/hourly-unaware (scale-be returns grey for GSC payment).
              // Missing -> 'grey', which the color map renders as amber, same
              // as the cases table. This eliminates any scale-be color leakage.
              base.plaintiff = [
                { label: 'Onboarding', color: delta.plaintiff_onboardingColor || 'grey', statusText: delta.plaintiff_onboardingStatus },
                { label: 'Payment',    color: delta.plaintiff_paymentColor || 'grey',    statusText: delta.plaintiff_paymentStatus },
                { label: c.briefText || 'Brief',      color: delta.plaintiff_briefColor || 'grey',      statusText: delta.plaintiff_briefStatus },
              ];
              base.defendant = [
                { label: 'Onboarding', color: delta.defendant_onboardingColor || 'grey', statusText: delta.defendant_onboardingStatus },
                { label: 'Payment',    color: delta.defendant_paymentColor || 'grey',    statusText: delta.defendant_paymentStatus },
                { label: c.briefText || 'Brief',      color: delta.defendant_briefColor || 'grey',      statusText: delta.defendant_briefStatus },
              ];
            }
            return base;
          });
          setMediatorCases(mappedCases);
        } else {
          // Scale-BE unavailable → fall back to the CM-BE shape exactly
          // as before. Same mapping the legacy code used.
          if (cm.analytics) {
            if (billingSummary) {
              // Invoiced revenue = paid + pending (incl. hourly). See the Scale
              // path above — backend mediatorRevenue is paid-only and undercounts.
              const invoicedRevenue =
                billingSummary.mediatorRevenueInvoiced ??
                ((Number(billingSummary.mediatorRevenue ?? billingSummary.collected) || 0) +
                  (Number(billingSummary.mediatorRevenueOutstanding ?? billingSummary.outstanding) || 0));
              cm.analytics.totalRevenueByAmount = invoicedRevenue;
              cm.analytics.totalRevenue = invoicedRevenue;
              cm.analytics.billingOutstanding = billingSummary.mediatorRevenueOutstanding ?? 0;
            }
            if (dashboardStats) {
              cm.analytics.totalCases = dashboardStats.totalCases ?? cm.analytics.totalCases;
              cm.analytics.casesBookedFromDash = dashboardStats.casesBooked ?? null;
            }
          }
          setMediator(cm);
          const mappedCases = (cm.cases || []).map((c) => ({
            ...mapCaseRow(c),
            case_manager_fee_amount: cmFeeForCase(c),
          }));
          setMediatorCases(mappedCases);
        }
      })
      .catch((err) => {
        if (mySeq !== fetchSeqRef.current) return;
        const status = err?.response?.status;
        if (status === 404) {
          setMediatorError({ kind: 'not_found', message: 'Mediator not found.' });
        } else if (status === 403) {
          setMediatorError({ kind: 'forbidden', message: 'You don’t have access to this mediator.' });
        } else {
          setMediatorError({ kind: 'unknown', message: 'Failed to load mediator. Try refreshing.' });
        }
        setMediator(null);
        setMediatorCases([]);
      })
      .finally(() => {
        if (mySeq !== fetchSeqRef.current) return;
        setLoading(false);
      });
  }, [id]);

  useEffect(() => { fetchMediator(); }, [fetchMediator]);

  // Reset transient per-mediator UI state whenever the route id changes
  // (e.g. user picks a different mediator from the FilterBar dropdown).
  // Without this, the previous mediator's openFirm, search query, page
  // number, AND advanced filters (selected coupons / firms / side)
  // persist — and since those selections refer to ids that don't exist
  // for the new mediator, the page silently filters everything out or
  // sits in firm-detail mode for a firm that has no cases here. The
  // page-level dateFilter intentionally persists because date windows
  // are conceptually independent of which mediator is being viewed.
  useEffect(() => {
    setOpenFirm(null);
    setOpenAttorney(null);
    setSearchQuery('');
    setCurrentPage(1);
    setAdvancedFilters({
      couponIds: new Set(),
      firmDomains: new Set(),
      side: 'all',
    });
    // We deliberately omit setAdvancedFilters from the deps array — it's
    // a stable useCallback and including it would re-run this effect on
    // every render. Same reasoning as the other reset setters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const initials = mediator?.name
    ? mediator.name.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2)
    : '';

  // Joined date = `createdAt` (a full timestamp) → render on the Pacific
  // business calendar (matching Scale Portal + the BE created_at bucketing),
  // not browser-local, so a late-UTC timestamp doesn't drift to the next day.
  const dateJoined = mediator?.createdAt
    ? new Date(mediator.createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', timeZone: 'America/Los_Angeles' })
    : '';

  // KPI source values are now computed via `kpi*` constants higher up in
  // this component — they fold in advanced-filter narrowing. Law firms
  // come from `appliedLawFirms` for the same reason. No more raw
  // BE-headline locals to keep in sync.

  // Search + pagination for Case Activities table. Source is
  // `appliedCases` so BOTH the date pill AND the advanced filters
  // narrow what's shown here.
  const q = searchQuery.trim().toLowerCase();
  const filtered = q
    ? appliedCases.filter((c) =>
        c.name.toLowerCase().includes(q) || c.id.toLowerCase().includes(q) || (c.caseNumber || '').toLowerCase().includes(q),
      )
    : appliedCases;

  const { widths: colWidths, setWidth: setColWidth } = useResizableColumns(
    'mediatorDetail.cases',
    { name: 342, date: 154, plaintiff: 280 }
  );
  const { sortedRows: sortedFiltered, sort: tableSort, requestSort: requestTableSort } = useSortableData(
    filtered,
    {
      name: (r) => r.name,
      date: (r) => r.dateRaw,
      plaintiff: (r) => (r.plaintiff || []).map((s) => s.color).join('|'),
      defendant: (r) => (r.defendant || []).map((s) => s.color).join('|'),
    }
  );
  const totalPages = Math.max(1, Math.ceil(sortedFiltered.length / pageSize));
  const safePage = Math.min(currentPage, totalPages);
  const pageRows = sortedFiltered.slice((safePage - 1) * pageSize, safePage * pageSize);

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  };

  // License # — prefer the real value when BE provides one; otherwise
  // derive a stable per-mediator code from mediator_id so the header
  // always shows the chip the design calls for.
  const licenseId = mediator?.licenseNumber
    || mediator?.license_number
    || (mediator?.mediatorId ? `CA-MED-${String(mediator.mediatorId).padStart(4, '0')}` : '');

  // Error state — rendered before the main page chrome so the user
  // sees a meaningful message instead of an empty profile + blank charts
  // when the BE returns 404/403/5xx.
  if (!loading && mediatorError) {
    return (
      <div className="flex flex-col min-h-screen bg-white">
        <div className="flex items-center justify-between border-b border-[#e5e5e5] bg-white" style={{ height: 60, padding: '14px 24px' }}>
          <span className="text-[#0e1e20] text-sm font-semibold" style={{ fontFamily: "'Inter Tight', sans-serif" }}>
            Mediator Analytics
          </span>
        </div>
        <div className="flex-1 flex flex-col items-center justify-center gap-4 p-8">
          <span className="text-[18px] font-semibold text-[#0e1e20]" style={{ fontFamily: "'Inter Tight', sans-serif" }}>
            {mediatorError.message}
          </span>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => navigate('/mediators')}
              className="rounded-lg border border-[#e5e5e5] bg-white px-4 py-2 text-[13px] font-medium text-[#0e1e20] hover:bg-[#f9fafb]"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              Back to mediators
            </button>
            {mediatorError.kind !== 'not_found' && mediatorError.kind !== 'forbidden' && (
              <button
                type="button"
                onClick={fetchMediator}
                className="rounded-lg bg-[#115e59] px-4 py-2 text-[13px] font-medium text-white hover:bg-[#0e4f4a]"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                Try again
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen relative bg-white">
      {loading && (
        <div className="absolute inset-0 bg-white/80 flex items-center justify-center" style={{ zIndex: 100 }}>
          <div className="flex flex-col items-center gap-3">
            <div className="w-8 h-8 border-2 border-[#115e59] border-t-transparent rounded-full animate-spin" />
            <span className="text-sm text-[#5b5a64] font-['Inter']">Loading...</span>
          </div>
        </div>
      )}

      {/* Top bar — page title + LOGGED-IN-USER avatar (not the mediator).
          Heights / padding match the canonical Dashboard/Mediators top bar. */}
      <div className="flex items-center justify-between border-b border-[#e5e5e5] bg-white" style={{ height: 60, padding: '14px 24px' }}>
        <span className="text-[#0e1e20] text-sm font-semibold" style={{ fontFamily: "'Inter Tight', sans-serif" }}>
          Mediator Analytics
        </span>
        <div className="w-8 h-8 rounded-full bg-[#115e59] flex items-center justify-center" title={user?.email || ''}>
          <span className="text-white text-xs font-semibold" style={{ fontFamily: 'Inter, sans-serif' }}>
            {(() => {
              const fn = user?.first_name || user?.firstName || '';
              const ln = user?.last_name || user?.lastName || '';
              const fromName = `${fn[0] || ''}${ln[0] || ''}`.toUpperCase();
              if (fromName) return fromName;
              const email = user?.email || '';
              return (email[0] || '?').toUpperCase();
            })()}
          </span>
        </div>
      </div>

      {/* Breadcrumb band — sits flush below the top bar, separated from
          the profile head by a full-width hairline divider. */}
      <div className="bg-white" style={{ padding: '14px 24px' }}>
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={() => navigate('/mediators')}
            className="text-[#5b5a64] text-[13px] hover:text-[#0e1e20]"
            style={{ fontFamily: 'Inter, sans-serif', background: 'transparent', border: 'none', padding: 0, cursor: 'pointer' }}
          >
            Mediators
          </button>
          <span className="text-[#9ca3af] text-[13px]">/</span>
          <span className="text-[#0e1e20] text-[13px] font-semibold" style={{ fontFamily: 'Inter, sans-serif' }}>
            {mediator?.name || ''}
          </span>
        </div>
      </div>
      <div className="border-b border-[#e5e5e5]" />

      {/* Profile head — avatar + name + meta on the left, Case Manager
          Fee card on the right. Matches the design screenshot exactly. */}
      <div className="bg-white flex items-center justify-between gap-4 border-b border-[#e5e5e5]" style={{ padding: '20px 24px' }}>
        <div className="flex items-center gap-4">
          <div className="rounded-full bg-[#115e59] flex items-center justify-center flex-shrink-0"
               style={{ width: 56, height: 56 }}>
            <span className="text-white text-base font-semibold tracking-tight" style={{ fontFamily: "'Inter Tight', sans-serif" }}>
              {initials || '—'}
            </span>
          </div>
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-2.5">
              <span className="text-[#0e1e20] text-[24px] font-semibold leading-tight"
                    style={{ fontFamily: "'Inter Tight', sans-serif" }}>
                {mediator?.name || ''}
              </span>
              {mediator?.status && (() => {
                const isActive = String(mediator.status).toLowerCase() === 'active';
                const bg = isActive ? '#e8fdf5' : '#f3f4f6';
                const dot = isActive ? '#00a666' : '#6b7280';
                const txt = isActive ? '#00a666' : '#374151';
                return (
                  <div className="inline-flex items-center gap-1.5 rounded-full" style={{ background: bg, padding: '3px 9px' }}>
                    <div className="rounded-full" style={{ width: 6, height: 6, background: dot }} />
                    <span className="text-[11px] font-medium" style={{ fontFamily: 'Inter, sans-serif', color: txt }}>
                      {mediator.status}
                    </span>
                  </div>
                );
              })()}
            </div>
            {/* Sub-row (Mediator · Joined · License #) removed per
                product feedback. */}
          </div>
        </div>
        {mediator && (
          <FeeEditor
            mediatorId={id}
            initialFeeType={mediator.case_manager_fee_type}
            initialFeeAmount={mediator.case_manager_fee_amount}
            onSaved={fetchMediator}
          />
        )}
      </div>

      {/* Detail tabs — Overview keeps the full analytics dashboard; the
          other three are the ported Scale-portal mediator tools. */}
      <div className="bg-white border-b border-[#e5e5e5]" style={{ padding: '0 24px' }}>
        <div className="flex items-center gap-1">
          {[
            { key: 'overview', label: 'Overview' },
            { key: 'booking', label: 'Booking Link' },
            { key: 'documents', label: 'Documents' },
            { key: 'profile', label: 'Profile' },
          ].map((t) => {
            const isActive = activeTab === t.key;
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => setActiveTab(t.key)}
                style={{
                  fontFamily: 'Inter, sans-serif',
                  fontSize: 13,
                  fontWeight: 600,
                  padding: '12px 14px',
                  border: 'none',
                  background: 'transparent',
                  cursor: 'pointer',
                  color: isActive ? '#115e59' : '#5b5a64',
                  borderBottom: `2px solid ${isActive ? '#115e59' : 'transparent'}`,
                  marginBottom: -1,
                }}
              >
                {t.label}
              </button>
            );
          })}
        </div>
      </div>

      {activeTab === 'booking' && (
        <div style={{ padding: '20px 24px 32px 24px' }}>
          <BookingLinkTab userId={mediator?.userId} notify={notify} />
        </div>
      )}

      {activeTab === 'documents' && (
        <div style={{ padding: '20px 24px 32px 24px' }}>
          <DocumentsTab mediatorId={id} notify={notify} />
        </div>
      )}

      {activeTab === 'profile' && (
        <div style={{ padding: '20px 24px 32px 24px' }}>
          <ProfileTab mediatorId={id} notify={notify} onSaved={fetchMediator} />
        </div>
      )}

      {activeTab === 'overview' && (
      <div style={{ padding: '20px 24px 32px 24px' }} className="flex flex-col gap-5">

        {/* Filter row — right-aligned. Date + Mediator pills only. The
            Advanced Filters section is rendered as a full-width
            expandable panel below this row (better visibility per UX
            advisory — no cramped popover). */}
        <div className="flex items-center justify-end gap-2 flex-wrap">
          <FilterBar
            className="!mb-0"
            dateValue={dateFilter}
            onDateChange={setDateFilter}
            dateOptions={DEFAULT_DATE_PRESETS}
            dateDefaultValue="all"
            customStart={customStart}
            customEnd={customEnd}
            onCustomDateChange={(s, e) => { setCustomStart(s); setCustomEnd(e); }}
            mediatorValue={mediator?.userId}
            onMediatorChange={(userId) => {
              if (!userId) return navigate('/mediators');
              const target = mediatorsList.find((m) => m.userId === userId);
              if (target && target.id) navigate(`/mediators/${target.id}`);
            }}
            mediators={mediatorsList}
          />
        </div>

        {/* Advanced Filters — sits at the top as its own section so the
            user can see and play with every filter at once. */}
        <AdvancedFiltersSection
          coupons={mediator?.coupons || []}
          firms={dateFilteredLawFirms}
          value={advancedFilters}
          onChange={setAdvancedFilters}
          isPending={isFiltering}
          onCouponExclude={async (couponId) => {
            // Soft-delete (exclude) the coupon for THIS mediator only.
            // BE flips is_deleted=true on the single row whose mediator_id
            // matches mediator.userId — guaranteed not to touch coupons
            // belonging to other mediators because the coupons table is
            // 1:1 with mediators via mediator_id FK.
            //
            // We send the mediator_id explicitly so the BE can cross-check
            // the coupon truly belongs to this mediator before mutating
            // (belt-and-suspenders against stale FE state / schema drift).
            const mediatorUserId = mediator?.userId;
            if (!mediatorUserId) {
              // eslint-disable-next-line no-alert
              window.alert('Cannot exclude — mediator not loaded.');
              return;
            }
            try {
              await api.patch(`/coupons/${couponId}/exclude`, null, {
                params: { mediator_id: mediatorUserId },
              });
              // Strip the excluded id from advanced filters so the active
              // filter set stays in sync.
              setAdvancedFilters((prev) => {
                if (!prev.couponIds.has(couponId)) return prev;
                const next = new Set(prev.couponIds);
                next.delete(couponId);
                return { ...prev, couponIds: next };
              });
              fetchMediator();
            } catch (err) {
              const msg = err?.response?.data?.message || 'Failed to exclude coupon.';
              // eslint-disable-next-line no-alert
              window.alert(msg);
            }
          }}
        />

        {/* KPI cards — Cases / Mediator Revenue / Cases Booked / Avg
            Booking Time. All values re-derive from `appliedCases` when
            any filter is active; otherwise they use the BE headline
            numbers so "All time" remains exact. */}
        <div className="flex gap-3 relative">
          {isFiltering && (
            <div className="absolute inset-0 z-10 flex items-center justify-center pointer-events-none"
                 style={{ background: 'rgba(255,255,255,0.55)' }}>
              <div className="flex items-center gap-2 rounded-full bg-white px-3 py-1.5"
                   style={{ boxShadow: '0 2px 8px rgba(16,24,40,0.12)' }}>
                <div className="rounded-full border-2 border-[#115e59] border-t-transparent animate-spin"
                     style={{ width: 14, height: 14 }} />
                <span className="text-[12px] font-medium" style={{ color: '#115e59', fontFamily: 'Inter' }}>
                  Updating…
                </span>
              </div>
            </div>
          )}
          <KpiCard
            title="Cases"
            value={kpiCases}
            change={kpiCasesChange}
            periodLabel={periodLabel}
            compareLabel={compareLabel}
          />
          <KpiCard
            title="Mediator Revenue"
            value={kpiRevenueAvailable ? fmtMoneyFull(kpiRevenue) : '—'}
            change={kpiRevenueChange}
            periodLabel={periodLabel}
            compareLabel={compareLabel}
          />
          <KpiCard
            title="Cases Booked"
            value={kpiCasesBookedChange ? kpiCasesBooked : kpiCasesBooked}
            change={kpiCasesBookedChange}
            periodLabel={periodLabel}
            compareLabel={compareLabel}
          />
          <KpiCard
            title="Avg Booking Time"
            value={avgBookingTimeLabel}
            change={kpiAvgBookingChange}
            invertColors
            periodLabel={periodLabel}
            compareLabel={compareLabel}
          />
        </div>

        {openFirm ? (
          /* ── Firm detail mode ─────────────────────────────────────
             When a firm is clicked, swap the entire content area for
             the firm detail page: header (Back button + firm summary)
             + per-case table for THIS firm only. The full Law Firms
             breakdown is intentionally hidden in this mode — use the
             "Back to Firms" button to return to the list view.
             Charts / AI Insights / Case Activities are hidden in this
             mode to match the design spec. */
          openAttorney ? (
            /* Attorney drill-down inside firm detail. Back returns to
               the firm cases list (clearing openAttorney). */
            <AttorneyDetailView
              email={openAttorney}
              cases={mediatorCases}
              onBack={() => setOpenAttorney(null)}
              onCaseClick={(caseId) => navigate(`/cases/${caseId}`)}
              domainOf={domainOf}
            />
          ) : (
            <FirmDetailView
              firm={openFirm}
              cases={appliedCases}
              side={advancedFilters.side}
              onBack={() => setOpenFirm(null)}
              onCaseClick={(caseId) => navigate(`/cases/${caseId}`)}
              onAttorneyClick={(email) => setOpenAttorney(email)}
            />
          )
        ) : (
        <>
        {/* Chart section — 2 rows of 2:
              Row 1: MoM Trend | Booking Trend
              Row 2: Inquiries Trend | Law Firms breakdown table
            The Law Firms table now occupies the bottom-right grid cell
            (replacing the previous tornado chart). If the table grows
            taller than the charts above, its grid cell will expand
            naturally — no fixed height is enforced. */}
        <div className="grid gap-3" style={{ gridTemplateColumns: '1fr 1fr' }}>
          <MoMTrendChart
            cases={appliedCases}
            dateRange={dateRange}
            granularity={momGranularity}
            onGranularityChange={setMomGranularity}
            subtitle={periodLabel && periodLabel !== 'All time'
              ? `Cases booked vs. income · ${periodLabel}`
              : undefined}
            onDrillDown={({ startDate, endDate }) => {
              const qs = new URLSearchParams({ startDate, endDate });
              if (mediator?.userId) qs.set('mediatorId', mediator.userId);
              navigate(`/cases?${qs.toString()}`);
            }}
          />
          <BookingTrendChart
            cases={appliedCases}
            dateRange={dateRange}
            periodLabel={periodLabel}
            granularity={bookingGranularity}
            onGranularityChange={setBookingGranularity}
            onDrillDown={({ startDate, endDate }) => {
              const qs = new URLSearchParams();
              if (mediator?.userId) qs.set('mediatorId', mediator.userId);
              if (startDate) qs.set('startDate', startDate);
              if (endDate) qs.set('endDate', endDate);
              navigate(`/cases?${qs.toString()}`);
            }}
          />
          {(() => {
            // ── Inquiries Over Time ───────────────────────────────────
            // Reuse the Dashboard's `InquiryConversionChart` (stacked
            // bar of total inquiries + line for conversion rate %) so
            // this card matches the Dashboard 1:1. Transform
            // `mediator.inquiriesMonthly` (length-12 trailing series
            // with `converted/pending/notConverted` COUNTS) into the
            // `inquiryConversion` shape the chart expects
            // (`labels/totals/converted%/buckets`).
            const series = Array.isArray(mediator?.inquiriesMonthly) ? mediator.inquiriesMonthly : [];
            // Page-level top filter takes precedence (same as MoM/Booking); the
            // per-chart dropdown applies only when the top filter is "All time".
            // NOTE: inquiriesMonthly is monthly-only, so a short range narrows to
            // the containing month(s), not daily bars (see isPartialMonth note).
            const inquiriesDateRange = (dateRange && (dateRange.startDate || dateRange.endDate))
              ? dateRange
              : chartRangeToDateRange(inquiriesChartRange);
            const rangeStart = inquiriesDateRange.startDate;
            const rangeEnd = inquiriesDateRange.endDate;
            const startYM = rangeStart ? rangeStart.slice(0, 7) : null;
            const endYM = rangeEnd ? rangeEnd.slice(0, 7) : null;
            // An inquiry can't be logged in the future, so never show months
            // beyond the current one — regardless of the selected range's end.
            const nowYM = (() => { const d = new Date(); return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`; })();
            const filteredSeries = series.filter((row) => {
              if (row.key > nowYM) return false;
              if (startYM && row.key < startYM) return false;
              if (endYM && row.key > endYM) return false;
              return true;
            });
            // BE returns inquiriesMonthly aggregated by calendar month —
            // there is no day-level series available. When the active
            // filter starts after the 1st of a month or ends before the
            // last, the bar for that month includes inquiries from days
            // outside the range. Surface this so the user understands the
            // chart is monthly-granular.
            const isPartialMonth = (() => {
              if (!rangeStart && !rangeEnd) return false;
              if (rangeStart && !rangeStart.endsWith('-01')) return true;
              if (rangeEnd) {
                const [y, m] = rangeEnd.split('-').map((n) => parseInt(n, 10));
                if (Number.isFinite(y) && Number.isFinite(m)) {
                  const lastDay = new Date(Date.UTC(y, m, 0)).getUTCDate();
                  if (rangeEnd.slice(-2) !== String(lastDay).padStart(2, '0')) return true;
                }
              }
              return false;
            })();
            const inquiryConversion = inquiriesGranularity === 'weekly'
              ? (() => {
                  // Weekly: bucket raw inquiry rows (created_at + status) into
                  // ≤16 week buckets, fit to the data — matches MoM/Booking.
                  const rawInq = Array.isArray(mediator?.inquiries) ? mediator.inquiries : [];
                  const inqDates = rawInq.map((q) => parseDateLoose(q.created_at)).filter(Boolean);
                  const wRange = (dateRange && (dateRange.startDate || dateRange.endDate))
                    ? dateRange
                    : fitWeekWindow(inqDates, { minWeeks: 8, maxWeeks: 16 });
                  const wb = buildWeekBuckets(wRange, 8, 16).map((b) => ({ ...b, converted: 0, pending: 0, notConverted: 0 }));
                  const byKey = new Map(wb.map((b) => [b.key, b]));
                  for (const q of rawInq) {
                    const d = parseDateLoose(q.created_at);
                    if (!d) continue;
                    const dow = (d.getUTCDay() + 6) % 7;
                    const start = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() - dow));
                    const key = `${start.getUTCFullYear()}-${pad2(start.getUTCMonth() + 1)}-${pad2(start.getUTCDate())}`;
                    const b = byKey.get(key);
                    if (!b) continue;
                    if (q.status === 'Converted') b.converted += 1;
                    else if (q.status === 'Not Converted') b.notConverted += 1;
                    else b.pending += 1;
                  }
                  return {
                    labels: wb.map((b) => b.label),
                    totals: wb.map((b) => b.converted + b.pending + b.notConverted),
                    converted: wb.map((b) => { const t = b.converted + b.pending + b.notConverted; return t > 0 ? Math.round((b.converted / t) * 100) : 0; }),
                    buckets: wb.map((b) => ({ label: b.label })),
                    overall: { total: wb.reduce((s, b) => s + b.converted + b.pending + b.notConverted, 0) },
                  };
                })()
              : {
              // Year-disambiguated labels: when the series spans more than
              // one calendar year (trailing 12 months almost always does),
              // appending the 2-digit year prevents two "Jan" bars from
              // sitting side-by-side with no way to tell them apart.
              labels: (() => {
                const yearsSeen = new Set(filteredSeries.map((row) => row.year));
                const multiYear = yearsSeen.size > 1;
                return filteredSeries.map((row) =>
                  multiYear
                    ? `${MONTH_SHORT[row.month]} ${String(row.year).slice(2)}`
                    : MONTH_SHORT[row.month]
                );
              })(),
              totals: filteredSeries.map((row) => (row.converted || 0) + (row.pending || 0) + (row.notConverted || 0)),
              converted: filteredSeries.map((row) => {
                const total = (row.converted || 0) + (row.pending || 0) + (row.notConverted || 0);
                return total > 0 ? Math.round(((row.converted || 0) / total) * 100) : 0;
              }),
              buckets: filteredSeries.map((row) => ({ label: MONTH_SHORT[row.month], year: row.year })),
              overall: { total: filteredSeries.reduce((s, r) => s + (r.converted || 0) + (r.pending || 0) + (r.notConverted || 0), 0) },
            };
            return (
              <div className="bg-white rounded-[10px] border border-[#e5e5e5] flex flex-col" style={{ height: 320 }}>
                <div className="flex items-start justify-between px-4 pt-3.5 pb-3 border-b border-[#e5e5e5]">
                  <div className="flex flex-col gap-0.5">
                    <span className="text-[#0e1e20] text-sm font-semibold leading-tight" style={{ fontFamily: "'Inter Tight', sans-serif" }}>
                      Inquiries Over Time
                    </span>
                    <span className="text-[#5b5a64] text-[11px]" style={{ fontFamily: 'Inter, sans-serif' }}>
                      {periodLabel && periodLabel !== 'All time'
                        ? `Stacked inquiries + conversion rate · ${periodLabel}`
                        : 'Stacked inquiries + conversion rate (last 12 months)'}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    {inquiriesGranularity === 'monthly' && isPartialMonth && (
                      <span
                        title="Inquiries are aggregated by calendar month. Bars for partially-covered months include days outside the selected range."
                        className="text-[10px] font-semibold rounded-md whitespace-nowrap"
                        style={{ padding: '3px 7px', background: '#fef3c7', color: '#92400e', fontFamily: 'Inter Tight' }}
                      >
                        Monthly aggregate
                      </span>
                    )}
                    <div className="inline-flex items-center rounded-lg p-0.5" style={{ background: '#f3f4f6' }}>
                      {['weekly', 'monthly'].map((g) => (
                        <button
                          key={g}
                          type="button"
                          onClick={() => setInquiriesGranularity(g)}
                          className="capitalize"
                          style={{
                            fontFamily: 'Inter, sans-serif',
                            fontSize: 11,
                            fontWeight: 600,
                            padding: '5px 12px',
                            borderRadius: 6,
                            border: 'none',
                            cursor: 'pointer',
                            background: inquiriesGranularity === g ? '#fff' : 'transparent',
                            color: inquiriesGranularity === g ? '#0e1e20' : '#5b5a64',
                            boxShadow: inquiriesGranularity === g ? '0 1px 2px rgba(16,24,40,0.08)' : 'none',
                          }}
                        >
                          {g}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="flex-1" style={{ padding: '8px 4px 12px 4px' }}>
                  <InquiryConversionChart inquiryConversion={inquiryConversion} navigate={navigate} height={250} />
                </div>
              </div>
            );
          })()}
          <LawFirmsTable firms={appliedLawFirms} selectedFirm={openFirm} onSelect={setOpenFirm} />
        </div>

        {/* Case Activities table */}
        <div className="bg-white rounded-[10px] border border-[#e5e5e5]">
          <div className="flex items-center justify-between px-4 py-3 border-b border-[#e5e5e5]">
            <span className="text-[#0e1e20] text-sm font-semibold" style={{ fontFamily: "'Inter Tight', sans-serif" }}>
              Case Activities
            </span>
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-2 px-3 rounded-lg border border-[#e5e5e5]" style={{ width: 280, height: 40, background: '#f3f4f6' }}>
                <svg width="16" height="16" viewBox="0 0 14 14" fill="none" style={{ flexShrink: 0 }}>
                  <circle cx="6.417" cy="6.417" r="4.667" stroke="#9ca3af" strokeWidth="1.167" />
                  <path d="M9.742 9.742l2.508 2.508" stroke="#9ca3af" strokeWidth="1.167" strokeLinecap="round" />
                </svg>
                <input
                  type="text" value={searchQuery} onChange={handleSearchChange}
                  placeholder="Search by case name or ID..."
                  style={{ border: 'none', outline: 'none', background: 'transparent', fontFamily: 'Inter, sans-serif', fontSize: 14, color: '#0e1e20', width: '100%' }}
                />
              </div>
              {/* Export button removed per product feedback. */}
            </div>
          </div>

          <div className="flex items-center bg-[#f9fafb] border-b border-[#f0f0f0]" style={{ height: 45 }}>
            <div className="flex items-center h-full px-4 relative" style={{ width: colWidths.name }}>
              <SortableHeader sortKey="name" sort={tableSort} onSort={requestTableSort} className="text-[#5b5a64] text-[12px] font-semibold" style={{ fontFamily: 'Inter, sans-serif', letterSpacing: '0.3px' }}>Case Name</SortableHeader>
              <ResizeHandle onResize={setColWidth('name')} getCurrentWidth={() => colWidths.name} />
            </div>
            <div className="flex items-center h-full px-3 relative" style={{ width: colWidths.date }}>
              <SortableHeader sortKey="date" sort={tableSort} onSort={requestTableSort} className="text-[#5b5a64] text-[12px] font-semibold" style={{ fontFamily: 'Inter, sans-serif', letterSpacing: '0.3px' }}>Mediation Date</SortableHeader>
              <ResizeHandle onResize={setColWidth('date')} getCurrentWidth={() => colWidths.date} />
            </div>
            <div className="flex items-center h-full px-3 relative" style={{ width: colWidths.plaintiff }}>
              {/* Plaintiff/Defendant Status are 3-dot indicators — sorting
                  by them ordered cases by a joined color string which
                  isn't meaningful to users. Render a plain header. */}
              <span className="text-[#5b5a64] text-[12px] font-semibold" style={{ fontFamily: 'Inter, sans-serif', letterSpacing: '0.3px' }}>Plaintiff Status</span>
              <ResizeHandle onResize={setColWidth('plaintiff')} getCurrentWidth={() => colWidths.plaintiff} />
            </div>
            <div className="flex items-center h-full px-3 flex-1">
              <span className="text-[#5b5a64] text-[12px] font-semibold" style={{ fontFamily: 'Inter, sans-serif', letterSpacing: '0.3px' }}>Defendant Status</span>
            </div>
          </div>

          {mediatorCases.length === 0 && !loading && (
            <div className="flex items-center justify-center" style={{ height: 120 }}>
              <span className="text-sm text-[#5b5a64]" style={{ fontFamily: 'Inter, sans-serif' }}>
                No cases assigned to this mediator yet.
              </span>
            </div>
          )}
          {/* Empty-state guard: distinguish "filtered out" from "search
              miss" so the user knows which lever to release. */}
          {mediatorCases.length > 0 && appliedCases.length === 0 && (
            <div className="flex items-center justify-center" style={{ height: 120 }}>
              <span className="text-sm text-[#5b5a64]" style={{ fontFamily: 'Inter, sans-serif' }}>
                No cases match the active filters. Try widening the date range or clearing advanced filters.
              </span>
            </div>
          )}
          {mediatorCases.length > 0 && appliedCases.length > 0 && filtered.length === 0 && (
            <div className="flex items-center justify-center" style={{ height: 120 }}>
              <span className="text-sm text-[#5b5a64]" style={{ fontFamily: 'Inter, sans-serif' }}>
                No cases match your search.
              </span>
            </div>
          )}

          {pageRows.map((row) => (
            <div
              key={row.id}
              role="button"
              tabIndex={0}
              className="flex items-center border-b border-[#f0f0f0] hover:bg-[#f9fafb] cursor-pointer last:border-b-0 focus:outline-none focus:bg-[#f3f4f6] transition-colors"
              style={{ height: 48 }}
              onClick={() => navigate(`/cases/${row.id}`)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate(`/cases/${row.id}`); }
              }}
            >
              <div className="flex items-center h-full px-4" style={{ width: colWidths.name }}>
                <span className="text-[#0e1e20] text-[13px] truncate" style={{ fontFamily: 'Inter, sans-serif' }}>{row.name}</span>
              </div>
              <div className="flex items-center h-full px-3" style={{ width: colWidths.date }}>
                <span className="text-[#0e1e20] text-[13px]" style={{ fontFamily: 'Inter, sans-serif' }}>{row.date}</span>
              </div>
              <div className="flex items-center gap-3 h-full px-3" style={{ width: colWidths.plaintiff }}>
                {row.plaintiff.map((s, i) => (
                  <div
                    key={i}
                    className="flex items-center gap-1"
                    title={s.statusText ? `${s.label}: ${s.statusText}` : s.label}
                  >
                    <StatusDot color={s.color} />
                    <span className="text-[#5b5a64] text-[11px]" style={{ fontFamily: 'Inter, sans-serif' }}>{s.label}</span>
                  </div>
                ))}
              </div>
              <div className="flex items-center gap-3 h-full px-3 flex-1">
                {row.defendant.map((s, i) => (
                  <div
                    key={i}
                    className="flex items-center gap-1"
                    title={s.statusText ? `${s.label}: ${s.statusText}` : s.label}
                  >
                    <StatusDot color={s.color} />
                    <span className="text-[#5b5a64] text-[11px]" style={{ fontFamily: 'Inter, sans-serif' }}>{s.label}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}

          <Pagination
            currentPage={safePage}
            totalPages={totalPages}
            totalEntries={sortedFiltered.length}
            pageSize={pageSize}
            onPageChange={setCurrentPage}
            onPageSizeChange={setPageSize}
          />
        </div>
        </>
        )}
      </div>
      )}

      {pageToast && (
        <Toast message={pageToast.message} type={pageToast.type} onDone={() => setPageToast(null)} />
      )}

    </div>
  );
}
